import { Inject, Injectable } from '@angular/core';
import { ConsumableViewModel } from '@app/modules/patient/patient-consultation/components/assessment/consumables/models/consumable-view-model.model';
import {
  DosageViewModel,
  formDosageMeasurement,
  MedicineViewModel,
} from '@app/modules/patient/patient-consultation/components/assessment/medicines/models/medicine-view-model.model';
import {
  convertMedicineToPrescriptionLine,
  dosageQuantityForms,
  enrichMedicineViewModelsInfoFlags,
  getDosageQuantity,
  getFriendlyDosageDescription,
  getFriendlyMedicationDescription,
} from '@app/modules/patient/patient-consultation/components/assessment/medicines/models/medicine-view-model.util';
import { patientAge } from '@app/shared/functions/patientAge';
import { action, arrayAdd, arrayRemove, arrayUpdate, arrayUpsert, transaction, withTransaction } from '@datorama/akita';
import * as _ from 'lodash';
import MeiliSearch, { Hits } from 'meilisearch';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, concat, EMPTY, forkJoin, from, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  delay,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  take,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import * as guidByString from 'uuid-by-string';
import * as uuidV4 from 'uuid/v4';
import { switchTap } from '../functions/switch-tap';
import { AlgoliaRestService } from './algolia-rest.service';
import {
  API_BASE_URL,
  CancelPatientVisit,
  ClinicalClient,
  ClinicalMetricVo,
  ClinicalNewClient,
  ClinicalPatientClient,
  CommunicationRenderRequest,
  CommunicationTemplateVo,
  CommunicationVo,
  Consumables,
  DiagnosisVo2,
  Dosages,
  EncounterDiagnosisTokenItemVo,
  EncounterDiagramVo,
  EncounterFollowUpVo,
  EncounterHeaderVoPlaceOfService,
  EncounterLineItemVo,
  EncounterPlanOutcomeVo,
  EncounterStatusVo,
  EncounterTemplateVo,
  EncounterVo,
  FormularyType,
  ICDPredictionRequest,
  MedicalCertificate,
  MedicalInsuranceVo,
  MedicationPrescription,
  MedicationPrescriptionLine,
  MedicineInfo,
  MedicineInteractionVo,
  Medicines,
  PatientConfigurationVo,
  PatientDashboardHistoryEventDto,
  PatientDocumentVo,
  PatientEventClient,
  PatientEventVo,
  PatientEventVoStatus,
  PatientFileNoteVo,
  PatientVo,
  ProviderCommunicationEmailVo,
  ProviderConfigurationVo,
  ProviderVo2,
  QuestionnaireVo,
  RestApiResultOfCompleteEncounterResult,
  RestApiResultOfEncounterVo,
  RestApiResultOfString,
  SmartCategories2,
  StaticCategory,
  Symptom,
  SymptomQuestion,
  SymptomQuestionAnswer,
  UpdateEncounterCommunication,
} from './api-client.service';
import { ApplicationInsightsService, AppInsightTags } from './application-insights.service';
import { PracticeProviderSettings } from './meraki-models/provider.models';
import { PharmacyEscriptRegistryService } from './escript/pharmacy-escript-registry.service';
import { CacheService } from './cache.service';
import { ConfigService } from './config.service';
import { MerakiClientService } from './meraki-client.service';
import { ERROR_CODE } from './meraki-models/error-models';
import { PatientsService, TimelineEventType } from './patients.service';
import { ProviderConfigurationKey, ProviderService } from './provider.service';
import { AccountNoRegex, ADMIN_MODIFIER_CODES, IClinicalMetricValue, ReferenceDataService } from './reference-data.service';
import {
  AllowedPpeConsultReasons,
  ConsultReason,
  EncounterType,
  HospitalReasons,
  PatientEventType,
  VirtualVisitTypes,
} from './state/clinical-encounter/clinical-encounter.model';
import { ClinicalEncountersQuery } from './state/clinical-encounter/clinical-encounter.query';
import {
  ClinicalEncountersStore,
  ClinicalEncounterUIViewModel,
  DEFAULT_CATEGORIES,
  MedicineInteractionStatus,
  SectionExpanded,
  SymptomQuestionSection,
} from './state/clinical-encounter/clinical-encounter.store';
import { EncounterLettersQuery } from './state/encounter-letters/encounter-letters.query';
import { EncounterLettersStore, Letter } from './state/encounter-letters/encounter-letters.store';
import { PatientStateModel } from './state/patients/patients.store';
import { PatientInWaitingRoomViewModel } from './state/provider/provider.store';
import { AdImpression } from './view-models/smartmeds-ads-models';

import { generateQuestionnairePdf$ } from '@app/modules/patient/patient-consultation/components/consultation-questionnaires/utils/generate-questionnaire-pdf';
import { CustomChargeCodes, CustomChargeCodeType } from './meraki-models/custom-charge-codes.models';
import { MerakiEncounterService } from './meraki/meraki-encounter.service';
import { MerakiLookupService } from './meraki/meraki-lookup.service';
import { MerakiPatientService } from './meraki/meraki-patient.service';

export interface SearchResult {
  Code: string;
  Description: string;
  Parameters: any;
}
type Key = string | number;
type KeyExtractor<T> = (item: T) => Key;
const babyMetricTypes = ['Weight', 'Height', 'BMI', 'Head-circumference'];
export const noDispensingWatermark = 'Copy of original: not for dispensing';

@Injectable({
  providedIn: 'root',
})
export class ClinicalEncounterService {
  inProgressEncounters$ = this.clinicalEncounterQuery.inProgressEncounters$;

  showQuickNotesOnEncounterSummary$ = new BehaviorSubject<boolean>(false);
  private readonly meiliSearchLookupClient!: MeiliSearch;

  constructor(
    private algoliaRestService: AlgoliaRestService,
    private referenceDataService: ReferenceDataService,
    private clinicalClient: ClinicalClient,
    private clinicalNewClient: ClinicalNewClient,
    private clinicalPatientClient: ClinicalPatientClient,
    private patientEventClient: PatientEventClient,
    private clinicalEncounterStore: ClinicalEncountersStore,
    private clinicalEncounterQuery: ClinicalEncountersQuery,
    private encounterLettersStore: EncounterLettersStore,
    private encounterLettersQuery: EncounterLettersQuery,
    private providerService: ProviderService,
    private patientsService: PatientsService,
    private cacheService: CacheService,
    private configService: ConfigService,
    private merakiPatientService: MerakiPatientService,
    private merakiService: MerakiClientService,
    private merakiLookupService: MerakiLookupService,
    private merakiEncounterService: MerakiEncounterService,
    private appInsightsService: ApplicationInsightsService,
    private escriptRegistry: PharmacyEscriptRegistryService,
    @Inject(API_BASE_URL) private baseUrl: string
  ) {
    this.meiliSearchLookupClient = new MeiliSearch({
      host: configService.config.meiliSearchLookup.host,
      apiKey: configService.config.meiliSearchLookup.key,
    });
  }

  encounterInProgressOrNulll$(practiceId: string, patientId: string) {
    return this.clinicalEncounterQuery.patientInProgressEncounter$(patientId);
  }

  encounterInProgress$(practiceId: string, patientId: string): Observable<EncounterStatusVo> {
    return this.clinicalEncounterQuery.patientInProgressEncounter$(patientId).pipe(filter(t => t != null));
  }

  getSmartTemplatesForDiagnosisTokens$(practiceId: string, patientId: string): Observable<SmartCategories2[]> {
    return this.encounterInProgress$(practiceId, patientId).pipe(
      map(encounter => encounter.Diagnosis || []),
      map(diagnoses => diagnoses.map(d => d.DiagnosisToken)),
      map(tokens => _.uniq(tokens.filter(token => !!token))),
      distinctUntilChanged(_.isEqual),
      map(tokens => (tokens || []).map(token => this.getSmartTemplates(practiceId, patientId, token))),
      switchMap(sts => forkJoin(sts)),
      map(sts => sts.filter(st => !!st)) // ignore smart template results that don't come back
    );
  }

  encounterInProgressUiState$(practiceId: string, patientId: string) {
    return this.clinicalEncounterQuery.ui.selectEntity(patientId);
  }

  upsetSectionExpandedValue(tenantId: string, patientId: string, updatedSection: SectionExpanded) {
    this.clinicalEncounterStore.ui.update(patientId, entity => {
      const updatedSections = arrayUpdate(
        entity.expansionSections,
        sectionsList => sectionsList.section === updatedSection.section,
        updatedSection
      );
      return {
        ...entity,
        expansionSections: updatedSections,
      };
    });
  }

  getMedicalCertificateLegacy(practiceId: string, encounterId: string) {
    return this.clinicalClient.getEncounter(practiceId, encounterId).pipe(
      withLatestFrom(this.providerService.provider$.pipe(take(1))),
      switchMap(([encounter, provider]) =>
        encounter.MedicalCertificate.MedicalCertificateId == '00000000-0000-0000-0000-000000000000'
          ? // if certificate is empty firebase storage doesn't contaisn proper report, use old way to generate certificate on the fly
            this.clinicalClient.getEncounter(practiceId, encounterId).pipe(
              switchMap(s => this.clinicalClient.generateMedicalCertificateReport(practiceId, s)),
              map(s => s.Data)
            )
          : this.getEncounterCommunicationBody(provider.PracticeNumber, encounter.MedicalCertificate.MedicalCertificateId)
      ),
      map(body => ({ Data: body }))
    );

    /*     return this.clinicalClient
          .getEncounter(practiceId, encounterId)
          .pipe(switchMap(s => this.clinicalClient.generateMedicalCertificateReport(practiceId, s))); */
  }

  getPrescriptionLegacy(practiceId: string, encounterId: string, prescriptionId: string) {
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider => this.getEncounterCommunicationBody(provider.PracticeNumber, prescriptionId)),
      map(body => ({ prescription: { Data: body } }))
    );

    // todo generate by prescriptionId, use method generatePrescriptionReportById
    /* return this.clinicalClient
      .getEncounter(practiceId, encounterId)
      .pipe(switchMap(encounter =>
        this.clinicalClient.generatePrescriptionReport(practiceId, encounter).pipe(
          map(prescription => ({ encounter, prescription }))
        )
      )); */
  }

  getPatientEncounterByVisit(practiceTenantId: string, providerId: string, patientId: string, patientVisit: PatientEventVo) {
    // load encounter from server and update local state
    return this.merakiEncounterService.getActiveEncounterForPatient(practiceTenantId, providerId, patientId, patientVisit).pipe(
      map(e => this.reorderEncounterLinesForEncounter(e)),
      tap(encounter => this.clinicalEncounterStore.addEncounter(patientId, encounter)),
      switchMap(encounter => {
        // check if need to preload sick note as prevSickNote to not lost user changes
        if (encounter.MedicalCertificate != null) {
          return this.getEncounterCommunicationBody(
            encounter.Provider.PracticeNumber,
            encounter.MedicalCertificate.MedicalCertificateId
          ).pipe(
            tap(body =>
              this.patientsService.updateSickNoteUIState(
                encounter.PatientEventDetails.Patient.PatientId,
                encounter.MedicalCertificate,
                body
              )
            ),
            mapTo(encounter)
          );
        }
        return of(encounter);
      }),
      withLatestFrom(this.referenceDataService.symptoms$.pipe(map(s => s.filter(f => f.Type === 'default')))),
      tap(([encounter, defaultComplaints]) => {
        // Ensure default complaint types(section notes) are included in consult.
        // Causes issues when consult is started & continued on separate browsers/machines
        const defaults = defaultComplaints.filter(f => !encounter.Symptoms?.map(m => m.SymptomId).includes(f.SymptomId));
        defaults.forEach(c => this.addSymptom(practiceTenantId, patientId, c));
      }),
      map(([encounter, _]) => encounter)
    );
  }

  getPatientEncounter(practiceId: string, patientId: string) {
    // load encounter from server and update local state
    return this.providerService.waitingRoom$.pipe(
      take(1),
      map(s => s.find(d => d.PatientId === patientId && d.PatientEvent.PracticeId === practiceId)),
      filter(s => s && s.PatientEvent.Status !== 1),
      withLatestFrom(this.providerService.provider$),
      switchMap(([s, provider]) =>
        this.getPatientEncounterByVisit(provider.PracticeTenantId, provider.PracticeId, s.PatientEvent.Patient.PatientId, s.PatientEvent)
      )
      // we're already doing on getPatientEncounterByVisit func
      /* map(e => this.reorderEncounterLinesForEncounter(e)),
      tap(encounter => this.clinicalEncounterStore.addEncounter(patientId, encounter)) */
    );
  }

  checkPatientEncounter(practiceId: string, patientId: string): Observable<boolean> {
    // todo refactor this method to directly request data from server, waiting room might be not up to date
    // due to signalR issues
    return this.providerService.provider$.pipe(
      switchMap(provider => this.patientsService.getActiveEncounter(provider.PracticeTenantId, practiceId, patientId, null)),
      map(encounter => encounter != null)
    );
  }

  startOrGetCurrentPatientEncounter(practiceId: string, patientId: string, consultReason: ConsultReason = ConsultReason.NormalConsult) {
    // HBC Go has no MyMPS waiting room — skip the waiting-list lookup and resolve the encounter directly.
    return this.providerService.isHbcGo$.pipe(
      take(1),
      switchMap(isHbcGo => {
        if (isHbcGo) {
          return this.providerService.provider$.pipe(
            take(1),
            switchMap(provider => this.patientsService.getActiveEncounter(provider.PracticeTenantId, practiceId, patientId, null)),
            switchMap(encounter => (encounter != null ? of(encounter) : this.startEncounter(practiceId, patientId, consultReason)))
          );
        }
        // add patient to waiting room & create encounter, return obs to create instance in akita
        return this.providerService.getWaitingList().pipe(
          distinctUntilChanged(_.isEqual),
          take(1),
          withLatestFrom(this.providerService.provider$),
          switchMap(([d, provider]) => {
            const waitingRoom = d?.find(room => room.PatientId === patientId && room.PatientEvent.PracticeId === practiceId);
            if (waitingRoom?.PatientEvent.Status === 2) {
              return this.getPatientEncounterByVisit(provider.PracticeTenantId, practiceId, patientId, waitingRoom.PatientEvent).pipe(
                // handle situation when for some reason we out of sync (visit in-progress but no encounter in firestore)
                catchError(s => of(null)),
                switchMap(s => (s == null ? this.startEncounter(practiceId, patientId, consultReason, waitingRoom) : of(s)))
              );
            } else if (waitingRoom == null) {
              return this.providerService.provider$.pipe(
                switchMap(provider => this.patientsService.getActiveEncounter(provider.PracticeTenantId, practiceId, patientId, null)),
                take(1),
                switchMap(encounter => (encounter != null ? of(encounter) : this.startEncounter(practiceId, patientId, consultReason)))
              );
            }

            return this.startEncounter(practiceId, patientId, consultReason, waitingRoom);
          })
        );
      })
    );
  }

  // HBC Go: resume the patient's active encounter (Akita, then Firestore directly —
  // bypassing onlyWithImportantInfo), or start a new one.
  startOrResumeEncounter$(tenantId: string, patientId: string, consultReason: ConsultReason): Observable<EncounterStatusVo> {
    return this.clinicalEncounterQuery.patientInProgressEncounter$(patientId).pipe(
      take(1),
      switchMap(akitaEncounter => {
        if (akitaEncounter) {
          return of(akitaEncounter);
        }
        return this.merakiEncounterService.getLatestActiveEncounterForPatient$(tenantId, patientId).pipe(
          switchMap(encounter => {
            if (encounter) {
              this.clinicalEncounterStore.addEncounter(patientId, encounter as EncounterStatusVo);
              return of(encounter as EncounterStatusVo);
            }
            // skipCheckin = true: HBC Go has no waiting room, so synthesise the walk-in in
            // memory (no CalendarEvent write). Seeded LineItems are stripped in completeEncounterHbcGo$.
            return this.startEncounter(tenantId, patientId, consultReason, undefined, true);
          })
        );
      })
    );
  }

  // HBC Go: simplified end-of-consult — skips main HBC's billing/diagnoses/follow-up.
  // Tenant split (see HBC-Go tenant rule): Firestore encounter writes use `tenantId`
  // (PracticeTenantId); the timeline-add and dashboard-refresh hit Florence and MUST use
  // `practiceId` (PracticeId) — PracticeTenantId there 403s and silently drops the timeline entry.
  completeEncounterHbcGo$(tenantId: string, patientId: string, practiceId: string): Observable<{ Sucess: boolean }> {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      map(encounter => {
        // encounterRef(PracticeId, EncounterId) throws "empty path" if either is missing.
        const fixed = this.fixEncounterObject(encounter);
        if (!fixed.EncounterId) {
          throw new Error(`completeEncounterHbcGo: EncounterId is missing (patientId=${patientId})`);
        }
        const withPractice = !fixed.PracticeId ? ({ ...fixed, PracticeId: tenantId } as typeof fixed) : fixed;
        // DateOfService = end-of-consult so the timeline note sorts/shows as the latest entry.
        // Scrub LineItems: the only line is the speciality default seeded by createEncounter
        // (HBC Go has no procedure UI), and each would otherwise become a timeline event.
        return {
          ...withPractice,
          EncounterLineItems: {
            ...withPractice.EncounterLineItems,
            EncounterHeader: {
              ...withPractice.EncounterLineItems?.EncounterHeader,
              DateOfService: new Date(),
            },
            LineItems: [],
          },
        } as typeof fixed;
      }),
      switchMap(encounter =>
        this.merakiEncounterService.saveEncounter(tenantId, encounter.EncounterId, encounter).pipe(
          switchMap(() => this.merakiEncounterService.updateEncounterStatus(tenantId, encounter.EncounterId, 'Completed')),
          switchMap(() =>
            this.patientsService.addPatientTimelineEventsForEncounter(practiceId, patientId, encounter).pipe(catchError(() => of(null)))
          ),
          // Best-effort dashboard refresh so the timeline tab shows the just-completed encounter.
          switchMap(() => this.patientsService.getPatientDashboard(practiceId, patientId).pipe(catchError(() => of(null)))),
          tap(() => {
            this.clinicalEncounterStore.remove(patientId);
            this.encounterLettersStore.remove(patientId);
          }),
          map(() => ({ Sucess: true })),
          catchError(err => {
            const detail = err?.message || err?.ResponseMessage || err?.error?.message || String(err);
            this.appInsightsService.trackException(err, 'completeEncounterHbcGo', {
              patientId,
              encounterId: encounter.EncounterId,
              errorCode: err?.code,
              errorDetail: detail,
            });
            console.error('[completeEncounterHbcGo] failed', { patientId, encounterId: encounter.EncounterId, err });
            throw err;
          })
        )
      )
    );
  }

  cancelPatientVisit(tenantId: string, patientEvent: PatientEventVo) {
    return this.providerService.provider$.pipe(
      withLatestFrom(this.providerService.isForge$),
      switchMap(([provider, isForge]) =>
        isForge
          ? this.merakiPatientService.cancelPatientVisit(provider.PracticeNumber, provider.HPCSANumber, patientEvent.PatientEventXRef)
          : this.patientEventClient.cancelPatientVisit(tenantId, {
              PatientEventId: patientEvent.PatientEventId,
              PatientEventXRef: patientEvent.PatientEventXRef,
              PracticeId: tenantId,
              Reason: 'Removed from waiting room',
              OriginatedSystem: 'ClinicianApp2.0',
            } as CancelPatientVisit)
      )
    );
  }

  cancelEncounterInProgress(practiceId: string, patientId: string) {
    // todo refactor to use patientVisitId from encounter as waitingRoom source can be not up to date due to signalR issues
    return this.providerService.waitingRoom$.pipe(
      take(1),
      map(s => s.find(d => d.PatientId === patientId && d.PatientEvent.PracticeId === practiceId)),
      withLatestFrom(this.providerService.provider$, this.providerService.isForge$),
      switchMap(([waitingRoom, provider, isForge]) =>
        of(waitingRoom).pipe(
          switchMap(waitingRoom => {
            if (waitingRoom == null) {
              return of({ Sucess: true });
            }
            const cancelVisit = {
              PatientEventId: waitingRoom.PatientEvent.PatientEventId,
              PatientEventXRef: waitingRoom.PatientEvent.PatientEventXRef,
              PracticeId: practiceId,
              Reason: 'Removed from waiting room',
              OriginatedSystem: 'ClinicianApp2.0',
            } as CancelPatientVisit;

            return (
              isForge
                ? this.merakiPatientService.cancelPatientVisit(provider.PracticeNumber, provider.HPCSANumber, cancelVisit.PatientEventXRef)
                : this.patientEventClient.cancelPatientVisit(practiceId, cancelVisit)
            ).pipe(tap(s => this.providerService.removeVisitFromWaitingRoom(cancelVisit.PatientEventId)));
          })
        )
      ),
      withLatestFrom(this.providerService.provider$, this.encounterInProgress$(practiceId, patientId).pipe(take(1))),
      switchTap(([response, provider, encounter]) =>
        this.merakiEncounterService.updateEncounterStatus(provider.PracticeTenantId, encounter.EncounterId, 'Cancelled')
      ),
      tap(([result, provider, encounter]) => {
        if (result.Sucess) {
          this.clinicalEncounterStore.remove(patientId);
        }
      }),
      catchError(error => {
        // if the above fails... there may be a bigger issue at play
        console.error('update status', error);
        this.clinicalEncounterStore.remove(patientId);
        return of(null);
      })
      // switchMap(t => this.providerService.getWaitingList())
    );
  }

  /**
   * Sent to ANY e-scripting vendor (Dis-Chem / ComputAssist / EasyRx / …) — such a prescription is
   * locked for edits; new medicine lines must go to a fresh prescription. Vendor-agnostic via the
   * descriptor registry (each descriptor declares its `sentField`).
   */
  private isSentToPharmacy(prescription: MedicationPrescription): boolean {
    return this.escriptRegistry.all().some(descriptor => !!(prescription as any)[descriptor.sentField]);
  }

  private reorderPrescriptionLines(prescriptionLines: MedicationPrescriptionLine[]) {
    const fApplyNewLineNumbers = (acc: MedicationPrescriptionLine[], item: MedicationPrescriptionLine) => {
      const i = { ...item }; // create new object ()
      i.LineNum = acc.length + 1;
      return [...acc, i];
    };
    return prescriptionLines.reduce(fApplyNewLineNumbers, []);
  }

  private reorderEncounterLinesForEncounter(encounter: EncounterVo) {
    return {
      ...encounter,
      EncounterLineItems: {
        ...encounter.EncounterLineItems,
        LineItems: this.reorderEncounterLines(encounter.EncounterLineItems.LineItems),
      },
    } as EncounterVo;
  }

  private reorderEncounterLines(encounterLines: EncounterLineItemVo[]) {
    // Create a new object with updated LineNum
    const assignNewLineNumbers = (acc: EncounterLineItemVo[], item: EncounterLineItemVo) => {
      const newItem = { ...item }; // Clone item
      newItem.LineNum = acc.length + 1;
      return [...acc, newItem];
    };

    // Filter lines for a single type and sort by original LineNum
    const filterLinesByType = (lines: EncounterLineItemVo[], type: string) =>
      lines.filter(line => line.LineType === type).sort((a, b) => a.LineNum - b.LineNum);

    // Define type order with grouped types
    const typeOrder = [
      ['Procedure', CustomChargeCodeType.ADMIN, CustomChargeCodeType.CSTM_PROCEDURE, 'Modifier'],
      ['Consumable', CustomChargeCodeType.CSTM_CONSUMABLE],
      ['Medicine', CustomChargeCodeType.CSTM_MEDICINE],
    ];

    // Reorder lines by type order, processing each type individually
    const reorderedLines = typeOrder
      .flatMap(group => group.flatMap(type => filterLinesByType(encounterLines, type)))
      .reduce(assignNewLineNumbers, []);

    return reorderedLines;
  }

  private startEncounter(
    practiceId: string,
    patientId: string,
    consultReason: ConsultReason,
    visit?: PatientInWaitingRoomViewModel,
    // HBC Go: when true, synthesise the walk-in PatientEventVo in memory and skip the
    // CalendarEvent Firestore write (checkinInProgressPatient). Required PatientEventDetails
    // fields still get embedded on the encounter for full-HBC upgrade compatibility.
    skipCheckin: boolean = false
  ): Observable<EncounterStatusVo> {
    // create encounter & return obs to it
    // return this.waitingRoomClient
    const encounter$ = this.patientsService.patientById$(patientId).pipe(
      take(1),
      switchMap(patient => {
        if (skipCheckin) {
          return of(this.synthesizeWalkInVisit(patient, practiceId, consultReason));
        }
        return visit == null
          ? this.checkinAndInProgressPatientVisit(patient, practiceId, consultReason)
          : this.markAsInProgress(practiceId, visit.PatientEvent);
      }), // if visit not null move to in-progress
      withLatestFrom(this.providerService.provider$),
      switchMap(([patientVisit, provider]) => from(this.createEncounter(consultReason, patientVisit, provider))),
      switchMap(encounter => this.merakiEncounterService.startCapturingEncounter(encounter.PracticeId, patientId, encounter))
    );

    return encounter$.pipe(
      // todo can be simplified and not required as it will created now on UI
      // switchMap(w => this.getPatientEncounterByVisit(practiceId, patientId, w.Data.PatientEventId)),
      map(e => this.reorderEncounterLinesForEncounter(e)),
      tap(encounter => this.clinicalEncounterStore.addEncounter(patientId, encounter)),
      withLatestFrom(this.referenceDataService.symptoms$.pipe(map(s => s.filter(f => f.Type === 'default')))),
      tap(([encounter, defaultComplaints]) => {
        // Ensure default complaint types(section notes) are included in consult.
        // Causes issues when consult is started & continued on separate browsers/machines
        const defaults = defaultComplaints.filter(f => !encounter.Symptoms?.map(m => m.SymptomId).includes(f.SymptomId));
        defaults.forEach(c => this.addSymptom(practiceId, patientId, c));
      }),
      map(([encounter, _]) => encounter),
      switchMap(s =>
        // load tokens for diagnosis without them (this diagnosis could be added by server side)
        forkJoin(s.Diagnosis?.filter(d => !d.DiagnosisToken).map(d => this.getDagnosisToken(d)) || []).pipe(
          defaultIfEmpty([]),
          tap(d => d.forEach(diagnosis => this.upsertDiagnosisVoToEncounter(practiceId, patientId, diagnosis)))
        )
      ),
      withLatestFrom(this.referenceDataService.symptoms$.pipe(map(s => s.filter(f => f.Type === 'default')))),
      tap(([, defaultComplaints]) => defaultComplaints.forEach(c => this.addSymptom(practiceId, patientId, c))),
      switchMap(() => this.setEncounterDefaults(practiceId, patientId)),
      withLatestFrom(this.providerService.providerConfigurations$),
      tap(([, providerConfigurations]) => this.updateEncounterFromProviderConfigs(practiceId, patientId, providerConfigurations)),
      switchMap(() => this.clinicalEncounterQuery.patientInProgressEncounter$(patientId)),
      withLatestFrom(this.providerService.provider$),
      switchTap(([encounter, provider]) => {
        return this.merakiService.isKahunPractice(provider).pipe(
          switchTap(result => {
            return result ? this.renderKahunPatientSummary(provider.PracticeNumber, patientId) : of(null);
          })
        );
      }),
      take(1) // detach obs, we need only called once
    );
  }

  markAsInProgress(practiceId: string, visit: PatientEventVo) {
    return this.providerService.provider$.pipe(
      withLatestFrom(this.providerService.isForge$),
      switchMap(([provider, isForge]) =>
        isForge
          ? this.merakiPatientService.markVisitInProgress(provider.PracticeNumber, visit).pipe(
              switchMap(s => this.patientsService.patientById$(visit.Patient.PatientId)),
              map(d => ({ ...visit, Patient: d.details }))
            )
          : this.patientEventClient
              .markInProgress(practiceId, visit.PatientEventId, { PatientEventDetails: visit })
              .pipe(mapTo({ ...visit, Status: PatientEventVoStatus._2 }))
      )
    );
  }

  // HBC Go: build the walk-in PatientEventVo in memory, no Firestore CalendarEvent doc.
  // Mirrors checkinAndInProgressPatientVisit's shape so downstream code (encounter creation,
  // PatientEventDetails embedding, completeEncounterHbcGo$) sees the same structure.
  private synthesizeWalkInVisit(patient: PatientStateModel, practiceId: string, consultReason: ConsultReason): PatientEventVo {
    const id = uuidV4();
    const visit = {
      PatientEventId: guidByString(id, 5),
      PatientEventXRef: id,
      PracticeId: practiceId,
      Patient: patient.details,
      CheckedIn: true,
      ScheduledTime: moment().utc().toDate(),
      CheckInTime: moment().utc().toDate(),
      Status: PatientEventVoStatus._2,
    } as PatientEventVo;

    if (consultReason === ConsultReason.MedicalInsurance) {
      visit.Type = PatientEventType.MedicalInsuranceType;
      visit.MedicalInsurance = { PatientIdentityNo: patient.details.PatientDetails.IdentityNo };
    }

    return visit;
  }

  checkinAndInProgressPatientVisit(patient: PatientStateModel, practiceId: string, consultReason: ConsultReason) {
    const id = uuidV4();
    const visit = {
      PatientEventId: guidByString(id, 5),
      PatientEventXRef: id,
      PracticeId: practiceId,
      Patient: patient.details,
      CheckedIn: true,
      ScheduledTime: moment().utc().toDate(),
      CheckInTime: moment().utc().toDate(),
    } as PatientEventVo;

    if (consultReason === ConsultReason.MedicalInsurance) {
      visit.Type = PatientEventType.MedicalInsuranceType;
      visit.MedicalInsurance = { PatientIdentityNo: patient.details.PatientDetails.IdentityNo };
    }

    return this.providerService.provider$.pipe(
      withLatestFrom(this.providerService.isForge$),
      switchMap(([provider, isForge]) =>
        isForge
          ? this.merakiPatientService
              .checkinInProgressPatient(
                provider.PracticeNumber,
                {
                  ...visit,
                  // add same what is doing nova for consistency (even so it's wrong way to store datetime)
                  ScheduledTime: moment().add(moment().utcOffset(), 'minutes').toDate(),
                  CheckInTime: moment().add(moment().utcOffset(), 'minutes').toDate(),
                },
                provider
              )
              .pipe(
                mapTo({
                  ...visit,
                  Status: PatientEventVoStatus._2,
                })
              )
          : this.patientEventClient
              .checkinPatient(practiceId, {
                PatientEventDetails: {
                  ...visit,
                  PatientEventXRef: null,
                },
              })
              .pipe(switchMap(s => this.markAsInProgress(practiceId, { ...visit, PatientEventXRef: null })))
      )
    );
  }

  private async createEncounter(consultReason: string, visit: PatientEventVo, provider: ProviderConfigurationVo) {
    const encounter = {
      Status: 'Created',
      EncounterId: visit.PatientEventId,
      PracticeId: provider.PracticeTenantId,
      Diagnosis: [],
      EncounterLineItems: {
        EncounterHeader: {
          DateOfService: visit.CheckInTime,
          TariffCode: 'Tarrif',
          ClaimType: 'Normal',
          ClaimAuthorizationNumber: '',
          HospitalIndicator: false,
          PlaceOfService: EncounterHeaderVoPlaceOfService._0,
          Diagnosis: [],
        },
        LineItems: [],
      },
      ConsultReason: consultReason,
      PatientEventDetails: { ...visit, Patient: { ...visit.Patient, PracticeId: provider.PracticeTenantId } } as PatientEventVo,
      SymptomQuestions: [],
      SymptomQuestionAnswers: [],
      Symptoms: [],
      MedicationsPrescriptions: [],
      AssistingDoctorName: null,
      AssistingDoctorPracticeNumber: null,
      ReferringDoctorName: null,
      ReferringDoctorPracticeNumber: null,
      BranchXRef: null,
      Provider: this.mapProviderConfiguration(provider),
    } as EncounterStatusVo;

    let diagnosisToAdd = null;
    if (HospitalReasons.includes(consultReason)) {
      encounter.EncounterLineItems.EncounterHeader.PlaceOfService = EncounterHeaderVoPlaceOfService._21;
    } else if (encounter.ConsultReason === ConsultReason.TelehealthConsult || VirtualVisitTypes.includes(encounter.ConsultReason)) {
      encounter.EncounterLineItems.EncounterHeader.PlaceOfService = EncounterHeaderVoPlaceOfService._2;
    } else if (encounter.ConsultReason === ConsultReason.VitalityAssessment) {
      diagnosisToAdd = 'Z00.0';
    } else if (encounter.ConsultReason === ConsultReason.ConditionsMedicationsDispense) {
      diagnosisToAdd = 'Z76.0';
    }

    if (encounter.PatientEventDetails.Type === PatientEventType.MedicalInsuranceType) {
      diagnosisToAdd = 'Z02.6';
      encounter.MedicalInsurance = encounter.PatientEventDetails.MedicalInsurance;

      const insurers = await this.providerService.loadMedicalInsurers$().toPromise();
      const insurer = insurers.find(s => s.Id == encounter.MedicalInsurance.MedicalInsurer?.Id);
      if (insurer != null) {
        encounter.MedicalInsurance.MedicalInsurer = insurer;
      }
      if (encounter.MedicalInsurance != null && encounter.MedicalInsurance.PatientIdentityNo != null) {
        encounter.MedicalInsurance.PatientIdentityNo = encounter.PatientEventDetails?.Patient?.PatientDetails?.IdentityNo;
      }
    } else {
      // todo add default procedure based on speciality rule
      // load speciality rule
      const specialityRule = await this.merakiLookupService.getSpecialityRule(provider.PracticeId, provider.SpecialityCode).toPromise();

      // honor the provider's default tariff code configured in Nova for the encounter's place of service
      let providerSettings: PracticeProviderSettings = null;
      let providerDefaultCode: string = null;
      if (provider.PracticeXRef?.startsWith('nova')) {
        try {
          const practiceProvider = await this.merakiLookupService
            .getPracticeProvider(provider.PracticeNumber, provider.HPCSANumber)
            .toPromise();
          providerSettings = practiceProvider?.Settings ?? null;
          providerDefaultCode =
            this.getProviderDefaultTariffCode(providerSettings, encounter.EncounterLineItems.EncounterHeader.PlaceOfService) || null;
        } catch (e) {
          console.error('failed to load nova provider settings', e);
          this.appInsightsService.trackException(e, 'createEncounter-providerSettings', { encounterId: encounter.EncounterId });
        }
      }

      let chargeCode = null;
      let defaultProcedureCode = providerDefaultCode || (specialityRule?.DefaultProcedureCode ?? '');

      if (
        !providerDefaultCode &&
        ((VirtualVisitTypes.includes(encounter.PatientEventDetails.Type) && defaultProcedureCode === '0190') ||
          encounter.ConsultReason === ConsultReason.TelephonicConsult)
      ) {
        defaultProcedureCode = '0130';
        /* chargeCode = {
          ChargeCode: '0130',
          ChargeDesc: 'Telephone consultation (all hours)'
        }; */
      }

      if (encounter.ConsultReason === ConsultReason.VitalityAssessment) {
        const age = patientAge(encounter.PatientEventDetails.Patient.PatientDetails.DateOfBirth);
        defaultProcedureCode = age.unitType === 'years' && age.unit > 18 ? '705255001' : 'VKIDS';
        chargeCode = null;
      }

      if (encounter.ConsultReason === ConsultReason.ConditionsMedicationsDispense) {
        defaultProcedureCode = null;
      }

      if (
        !providerDefaultCode &&
        (encounter.ConsultReason === ConsultReason.VideoConsult || encounter.PatientEventDetails.Type === ConsultReason.VideoConsult)
      ) {
        defaultProcedureCode = 'VCONS';
        /* chargeCode = {
          ChargeCode: 'VCONS',
          ChargeDesc: 'Real time/Synchronous video consultation'
        }; */
      }

      if (consultReason === ConsultReason.AdhocPrescription) {
        defaultProcedureCode = '0132';
      } else if (consultReason === ConsultReason.AdhocSickNote) {
        const medicalCertificate = {
          MedicalCertificateId: uuidV4(),
          CreatedDate: new Date(),
          ExaminationDate: new Date(),
          FromDate: new Date(),
          ToDate: null,
          Provider: encounter.Provider,
          Patient: encounter.PatientEventDetails.Patient,
          Diagnoses: [],
        } as MedicalCertificate;
        encounter.MedicalCertificate = medicalCertificate;
      }

      if ((specialityRule != null || !!providerDefaultCode) && chargeCode == null && !!defaultProcedureCode) {
        // search procedures from nova source for all (align with procedure change)
        try {
          const types = await this.referenceDataService.tarrifMedicalTypeCodes$.toPromise();
          let procedures = await this.getTariffCode(this.getTarrifMedicalType(types, provider.SpecialityCode), defaultProcedureCode);

          if (!procedures?.length && defaultProcedureCode === providerDefaultCode && specialityRule?.DefaultProcedureCode) {
            // nova-configured code didn't resolve to a valid tariff, fall back to the speciality default
            providerDefaultCode = null;
            defaultProcedureCode = specialityRule.DefaultProcedureCode;
            procedures = await this.getTariffCode(this.getTarrifMedicalType(types, provider.SpecialityCode), defaultProcedureCode);
          }

          if (procedures && procedures.length > 0) {
            chargeCode = {
              ChargeCode: procedures[0].Code,
              ChargeDesc: procedures[0].Description,
            };
          }
        } catch (e) {
          console.error('failed to load default procedure code', e);
        }
      }

      const excludeDefaultCode = this.providerService.getExcludeProcedureCode(provider.Options);
      // setting allow to ignore any pre-default logic, except adhoc prescription
      if (excludeDefaultCode && consultReason !== ConsultReason.AdhocPrescription) {
        chargeCode = null;
      }

      if (chargeCode != null) {
        encounter.EncounterLineItems.LineItems = [this.buildDefaultProcedureLine(chargeCode)];
        this.appInsightsService.trackEvent(AppInsightTags.DEFAULT_PROCEDURE_CODE_APPLIED, {
          providerId: provider.TreatingPracticeNumber,
          encounterId: encounter.EncounterId,
          placeOfService: `${encounter.EncounterLineItems.EncounterHeader.PlaceOfService}`,
          novaConfiguredCode: providerDefaultCode ?? '',
          finalCode: chargeCode.ChargeCode,
          source:
            providerDefaultCode && defaultProcedureCode === providerDefaultCode
              ? 'Nova provider Place-of-Service setting'
              : 'HbC speciality fallback',
        });
      }

      const optionCode = encounter.PatientEventDetails.Patient?.PatientAccountDetails?.MedicalAidOptionCode;
      const isPpeEnabled = this.providerService.isPpeEnabled(provider.Options) || !!providerSettings?.AlwaysAddPpeCodeToInvoice;
      const allowedConsultReason = AllowedPpeConsultReasons.includes(encounter.ConsultReason);

      if (isPpeEnabled && !excludeDefaultCode && allowedConsultReason && optionCode) {
        const ppeCodes = await this.merakiLookupService.getPpeCodes(optionCode, encounter.Provider.SpecialityCode).toPromise();

        if (ppeCodes?.Tariffs?.length > 0) {
          const chargeCodes = [];
          for (const tariff of ppeCodes?.Tariffs) {
            const procedures = await this.searchProcedures(
              provider.PracticeId,
              tariff.TariffCode,
              encounter.EncounterLineItems.EncounterHeader.DateOfService
            ).toPromise();
            if (procedures.length > 0) {
              chargeCodes.push({
                ChargeCode: procedures[0].Code,
                ChargeDesc: procedures[0].Description,
              });
            }
          }

          if (chargeCodes.length > 0) {
            encounter.EncounterLineItems.LineItems = [
              ...encounter.EncounterLineItems.LineItems,
              ...chargeCodes.map(s => this.buildDefaultProcedureLine(s)),
            ];
          }
        }
      }
    }

    const result = (diagnosisToAdd && (await this.algoliaRestService.getDiagnosis(diagnosisToAdd))) || null;
    if (result != null) {
      const diagnosis = {
        PrimaryIndicator: true,
        DiagnosisCode: result?.ICD10Code || null,
        DiagnosisDescription: result?.ICD10CodeDescription || null,
        ValidPrimary: result?.ValidAsPrimary === 1 || false,
        ValidSequelae: result?.ValidAsSequalae === 1 || false,
        Gender: result?.Gender || '',
        /* AgeRangeStart: result?.AgeRange, // todo get properly range
        AgeRangeEnd: result?.AgeRange, */
      } as DiagnosisVo2;

      encounter.EncounterLineItems.EncounterHeader.Diagnosis = [diagnosis];
      encounter.Diagnosis = [diagnosis];
      encounter.EncounterLineItems.LineItems = encounter.EncounterLineItems.LineItems.map(
        s =>
          ({
            ...s,
            Diagnosis: [diagnosis],
          }) as EncounterLineItemVo
      );
    }

    encounter.EncounterType = this.getEncounterType(encounter);
    encounter.BranchXRef = encounter.PatientEventDetails.BranchXRef || null;
    encounter.RequiresFollowUp = provider.ManualPricingSchemes?.includes('*') || false;

    return encounter;
  }

  private mapProviderConfiguration(provider: ProviderConfigurationVo): ProviderVo2 {
    return {
      CellphoneNumber: provider.CellphoneNumber,
      DispensingLicenseNumber: provider.DispensingDoctor,
      PracticeType: 'Single',
      TreatingDoctorPracticeNumber: provider.TreatingPracticeNumber,
      ContactNumber: provider.ContactNumber,
      DispensingDoctor: provider.DispensingDoctor,
      EmailAddress: provider.EmailAddress,
      PracticeName: provider.PracticeName,
      PracticeNumber: provider.PracticeNumber,
      SpecialityCode: provider.SpecialityCode,
      SpecialityDescription: provider.SpecialityDescription,
      FaxNumber: provider.FaxNumber,
      Qualification: provider.Qualification,
      HPCSANumber: provider.HPCSANumber,
      TreatingDoctorName: provider.TreatingDoctorName,
      IsLocumProvider: provider.IsLocumProvider,
      PhysicalAddress: provider.PhysicalAddress,
      PostalAddress: provider.PostalAddress,
      SubSpecialityCode: provider.SubSpecialityCode,
      TelephoneNumber: provider.TelephoneNumber,
    };
  }

  private getProviderDefaultTariffCode(settings: PracticeProviderSettings, placeOfService: EncounterHeaderVoPlaceOfService): string {
    switch (placeOfService) {
      case EncounterHeaderVoPlaceOfService._21:
        return settings?.DefaultTariffCodeForInpatientHospital;
      case EncounterHeaderVoPlaceOfService._24:
        return settings?.DefaultTariffCodeForDayHospital;
      case EncounterHeaderVoPlaceOfService._2:
        return settings?.DefaultTariffCodeForTelemedicine;
      // unknown place of service at encounter creation is billed as consulting rooms
      case EncounterHeaderVoPlaceOfService._11:
      case EncounterHeaderVoPlaceOfService._0:
        return settings?.DefaultTariffCodeForConsultingRoom;
      default:
        return null;
    }
  }

  private buildDefaultProcedureLine(chargeCode: any): EncounterLineItemVo {
    return {
      ...chargeCode,
      LineType: 'Procedure',
      ChargeQuan: 1,
      ChargeUnits: 'Units',
      TotalIncVat: 0,
      UnitPrice: 0,
      TotalExcVat: 0,
      VatAmount: 0,
      Parameters: [],
    };
  }

  getEncounterType(encounter: EncounterStatusVo): string {
    if (encounter.PatientEventDetails.Type === PatientEventType.MedicalInsuranceType) {
      return EncounterType.MedicalInsurance;
    }

    if (encounter.PatientEventDetails.Type === PatientEventType.NoChargeType) {
      return EncounterType.ZeroInvoice;
    }

    if (encounter.PatientEventDetails.Patient.PatientAccountDetails.IsCashAccount || encounter.PatientEventDetails.IsCashAccount) {
      return EncounterType.CashInvoice;
    }

    return EncounterType.MedicalAidInvoice;
  }

  getSmartTemplateStaticCategories(practiceId: string) {
    const cacheKey = `getSmartTemplateStaticCategories-${practiceId}`;
    const cache = this.cacheService.Get<StaticCategory[]>(cacheKey);
    if (!!cache) {
      return of(cache);
    } else {
      return this.clinicalNewClient.getSmartTemplatesStaticCategories(practiceId).pipe(
        tap(result => {
          this.cacheService.Set<StaticCategory[]>(cacheKey, result, { TTL: 1000 * 60 * 60 }); // cache for 60 minutes
        })
      );
    }
  }

  getSmartTemplates(practiceId: string, patientId: string, token: string) {
    return this.clinicalEncounterQuery.ui.selectEntity(patientId).pipe(
      take(1),
      map(uiState => uiState?.diagnosisSuggestions || {}),
      switchMap(cache => {
        if (!!cache[token]) {
          // Cache hit
          const val = cache[token];
          return of(val);
        }
        // Cache miss
        return this.clinicalNewClient.searchSmartTemplates(practiceId, token).pipe(
          catchError(err => of(null)),
          filter(s => !!s),
          tap(templates => {
            this.clinicalEncounterStore.ui.update(patientId, uiState => {
              const newState = {
                ...uiState,
                diagnosisSuggestions: {
                  ...uiState.diagnosisSuggestions,
                  [token]: templates,
                },
              };
              return newState;
            });
          })
        );
      })
    );
  }

  getSmartTemplatesMedicines(practiceId: string, patientId: string, token: string): Observable<Medicines[]> {
    const cacheKey = `getSmartTemplatesStaticCategoryMedicines-${practiceId}-${token}`;
    const cache = this.cacheService.Get<Medicines[]>(cacheKey);
    if (!!cache) {
      return of(cache);
    } else {
      return this.clinicalNewClient.getSmartTemplatesStaticCategoryMedicines(practiceId, token).pipe(
        tap(result => {
          this.cacheService.Set<Medicines[]>(cacheKey, result, { TTL: 1000 * 60 * 5 }); // cache for 5 minutes
        })
      );
    }
  }

  getSmartTemplatesConsumables(practiceId: string, patientId: string, token: string): Observable<Consumables[]> {
    const cacheKey = `getSmartTemplatesDynamicCategoryConsumables-${practiceId}-${token}`;
    const cache = this.cacheService.Get<Consumables[]>(cacheKey);
    if (!!cache) {
      return of(cache);
    } else {
      return this.clinicalNewClient.getSmartTemplatesStaticCategoryConsumables(practiceId, token).pipe(
        tap(result => {
          this.cacheService.Set<Consumables[]>(cacheKey, result, { TTL: 1000 * 60 * 5 }); // cache for 5 minutes
        })
      );
    }
  }

  generateCommunicationReports(
    tenantId: string,
    patientId: string,
    encounterId: string,
    communications: CommunicationVo[]
  ): Observable<RestApiResultOfString> {
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider =>
        forkJoin([...communications.map(s => this.getEncounterCommunicationBody(provider.PracticeNumber, s.CommunicationId))])
      ),
      switchMap(bodies => {
        const date = new Date();
        const document = {
          PracticeId: tenantId,
          PatientId: patientId,
          Category: 'EmailAttachment',
          FileName: `${encounterId}_batch_documents.pdf`,
          CapturedDate: date,
          DocumentDate: date,
          DocumentId: encounterId,
        };

        return this.clinicalClient.captureAsSingleDocumentPdf(tenantId, encounterId, patientId, {
          Htmls: bodies,
          Document: document,
          ReplaceExistingDocument: true,
        });
      })
    );
  }

  // getMedicine(practiceId: string, code: string) {
  //   return this.clinicalNewClient.searchMedicine(practiceId, code);
  // }

  getDosages(practiceId: string, atc: string, productForm: string, strength: string) {
    try {
      // Product strength can sometimes be an empty string. We need to explicity set it to 0 if it is not provided.
      strength = strength || '0';
      productForm = productForm || '?';
      // Ensure only numbers appear in strength, sometimes we get values of 40/60
      strength = strength.match(/^[0-9]+$/) ? strength : '0';
      return this.clinicalNewClient.getMedicineDosages(practiceId, atc, productForm, strength);
    } catch {
      return new Observable<Dosages>();
    }
  }

  getDosagesByMGIC(practiceId: string, mgic: string) {
    return this.clinicalNewClient.getMedicineDosagesByMGIC(practiceId, mgic);
  }

  upsertDiagnosisTokenToEncounter(tenantId: string, patientId: string, diagnosisToken: EncounterDiagnosisTokenItemVo) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      DiagnosisTokenItems: arrayUpsert(entity.DiagnosisTokenItems || [], diagnosisToken.DiagnosisToken, diagnosisToken, 'DiagnosisToken'),
    }));
  }

  removeDiagnosisTokenFromEncounter(tenantId: string, patientId: string, diagnosisToken: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      DiagnosisTokenItems: arrayRemove(entity.DiagnosisTokenItems, m => m.DiagnosisToken === diagnosisToken),
    }));
  }

  capturePatientFileNote(providerId: string, patientId: string, note: PatientFileNoteVo) {
    return this.clinicalNewClient.capturePatientFileNote(providerId, patientId, note);
  }

  upsertDiagnosisVoToEncounter(tenantId: string, patientId: string, diagnosis: DiagnosisVo2) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const diagnoses = arrayUpsert(entity.Diagnosis || [], diagnosis.DiagnosisCode, diagnosis, 'DiagnosisCode');
      return {
        Diagnosis: diagnoses,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          EncounterHeader: {
            ...entity.EncounterLineItems.EncounterHeader,
            Diagnosis: diagnoses,
          },
          LineItems: entity.EncounterLineItems.LineItems.map(m => ({
            ...m,
            Diagnosis: arrayUpsert(m?.Diagnosis || [], diagnosis.DiagnosisCode, diagnosis, 'DiagnosisCode'),
          })),
        },
        MedicationsPrescriptions: entity.MedicationsPrescriptions.map(m => ({
          ...m,
          MedicationPrescriptionLines: m.MedicationPrescriptionLines.map(mp => ({
            ...mp,
            Diagnosis: arrayUpsert(mp?.Diagnosis || [], diagnosis.DiagnosisCode, diagnosis, 'DiagnosisCode'),
          })),
        })),
      };
    });
  }

  removeDiagnosisCodeFromEncounter(tenantId: string, patientId: string, diagnosisCode: string) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const diagnoses = arrayRemove(entity.Diagnosis || [], m => m.DiagnosisCode === diagnosisCode);
      return {
        Diagnosis: diagnoses,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          EncounterHeader: {
            ...entity.EncounterLineItems.EncounterHeader,
            Diagnosis: diagnoses,
          },
          LineItems: entity.EncounterLineItems.LineItems.map(m => ({
            ...m,
            Diagnosis: m.Diagnosis.filter(f => f.DiagnosisCode !== diagnosisCode),
          })),
        },
        MedicationsPrescriptions: entity.MedicationsPrescriptions.map(m => ({
          ...m,
          MedicationPrescriptionLines: m.MedicationPrescriptionLines.map(mp => ({
            ...mp,
            Diagnosis: mp.Diagnosis.filter(f => f.DiagnosisCode !== diagnosisCode),
          })),
        })),
      };
    });
  }

  setDiagnosisAsPrimary(tenantId: string, patientId: string, diagnosisCode: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      Diagnosis: this.reorderDiagnosesForPrimary(entity.Diagnosis, diagnosisCode),
    }));
  }

  private reorderDiagnosesForPrimary(diagnoses: DiagnosisVo2[], primaryCode: string) {
    const sortedDiags = _.orderBy(diagnoses, d => (d.DiagnosisCode === primaryCode ? 0 : 1));
    return sortedDiags;
  }

  arrayUpsertItem<T>(array: T[], keyExtractor: KeyExtractor<T>, newItem: T): T[] {
    var result = [...array];
    const newItemKey = keyExtractor(newItem);
    const index = result.findIndex(item => keyExtractor(item) === newItemKey);

    if (index > -1) {
      result[index] = newItem;
    } else {
      result.push(newItem);
    }

    return result;
  }

  addProcedureToEncounter(tenantId: string, patientId: string, token: string, line: EncounterLineItemVo) {
    // exclude default procedures from incrementing quantity
    return this.providerService.specialityRule$.pipe(
      take(1),
      tap(specialityRule => {
        const defaultProcedure = specialityRule.DefaultProcedureCode;
        this.clinicalEncounterStore.update(patientId, entity =>
          this.reorderEncounterLinesForEncounter({
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: this.arrayUpsertItem(
                entity.EncounterLineItems.LineItems,
                item => item.ChargeCode + item.LineType,
                {
                  LineType: line.LineType,
                  LineNum: 99,
                  ChargeCode: line.ChargeCode,
                  ChargeDesc: line.ChargeDesc,
                  ChargeQuan: line.ChargeCode !== defaultProcedure ? this.getEncounterLineQuantitySum(entity, line) : 1,
                  UnitPrice: line.UnitPrice,
                  TotalExcVat: 0,
                  TotalIncVat: 0,
                  Amount:
                    line.LineType == CustomChargeCodeType.CSTM_PROCEDURE
                      ? (line.UnitPrice * this.getEncounterLineQuantitySum(entity, line)) / 100
                      : null,
                  PriceOverride: line.PriceOverride,
                  Diagnosis: entity.EncounterLineItems.EncounterHeader.Diagnosis,
                  Parameters: {
                    Token: token,
                  },
                } as EncounterLineItemVo
                //'ChargeCode',
              ),
            },
          })
        );
      })
    );
  }

  private getEncounterLineQuantitySum(entity: EncounterVo, item: EncounterLineItemVo): number {
    const line =
      item.LineType === 'Procedure'
        ? entity.EncounterLineItems.LineItems.find(it => it.ChargeCode === item.ChargeCode && it.LineType === 'Procedure')
        : entity.EncounterLineItems.LineItems.find(it => it.NappiCode === item.NappiCode);

    const quantity = (line && line.ChargeQuan + item.ChargeQuan) || item.ChargeQuan || 1;

    return quantity;
  }

  addMedicineToEncounter(tenantId: string, patientId: string, medicine: EncounterLineItemVo) {
    this.clinicalEncounterStore.update(patientId, entity =>
      this.reorderEncounterLinesForEncounter({
        ...entity,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          LineItems: arrayUpsert(
            entity.EncounterLineItems.LineItems,
            medicine.NappiCode,
            {
              LineType: medicine.LineType || 'Medicine',
              LineNum: 99,
              ChargeCode: '0197',
              ...medicine,
              UnitPrice: medicine.UnitPrice,
              TotalExcVat: 0,
              TotalIncVat: 0,
              Amount:
                medicine.LineType == CustomChargeCodeType.CSTM_MEDICINE
                  ? (medicine.UnitPrice * this.getEncounterLineQuantitySum(entity, medicine)) / 100
                  : null,
              PriceOverride: medicine.PriceOverride,
              ChargeQuan: this.getEncounterLineQuantitySum(entity, medicine),
              Diagnosis: (medicine.Diagnosis?.length && medicine.Diagnosis) || entity.EncounterLineItems.EncounterHeader.Diagnosis,
            } as EncounterLineItemVo,
            'NappiCode'
          ),
        },
      })
    );
  }

  addAdminToEncounter(tenantId: string, patientId: string, line: EncounterLineItemVo) {
    this.clinicalEncounterStore.update(patientId, entity =>
      this.reorderEncounterLinesForEncounter({
        ...entity,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          LineItems: arrayUpsert(
            entity.EncounterLineItems.LineItems,
            line.ChargeCode,
            {
              LineType: line.LineType,
              LineNum: 99,
              ChargeCode: line.ChargeCode,
              ChargeDesc: line.ChargeDesc,
              UnitPrice: line.UnitPrice,
              TotalExcVat: 0,
              TotalIncVat: 0,
              Amount: (line.UnitPrice * this.getEncounterLineQuantitySum(entity, line)) / 100,
              PriceOverride: line.PriceOverride,
              ChargeQuan: this.getEncounterLineQuantitySum(entity, line),
              Diagnosis: (line.Diagnosis?.length && line.Diagnosis) || entity.EncounterLineItems.EncounterHeader.Diagnosis,
            } as EncounterLineItemVo,
            'ChargeCode'
          ),
        },
      })
    );
  }

  addCustomMedicineToEncounter(tenantId: string, patientId: string, medicine: CustomChargeCodes) {
    return of(medicine).pipe(
      tap(m => {
        this.clinicalEncounterStore.update(patientId, entity =>
          this.reorderEncounterLinesForEncounter({
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: arrayUpsert(
                entity.EncounterLineItems.LineItems,
                medicine.Code,
                {
                  LineType: CustomChargeCodeType.CSTM_MEDICINE,
                  LineNum: 99,
                  ChargeCode: '0197',
                  ChargeDesc: medicine.Description,
                  UnitPrice: medicine.UnitPrice,
                  TotalExcVat: 0,
                  TotalIncVat: 0,
                  Amount: (medicine.UnitPrice * medicine.PackSize) / 100,
                  PriceOverride: true,
                  ChargeQuan: medicine.PackSize,
                  Diagnosis: entity.EncounterLineItems.EncounterHeader.Diagnosis,
                } as EncounterLineItemVo,
                'NappiCode'
              ),
            },
          })
        );
      })
    );
  }

  addCustomConsumableToEncounter(tenantId: string, patientId: string, item: CustomChargeCodes) {
    return of(item).pipe(
      tap(m => {
        this.clinicalEncounterStore.update(patientId, entity =>
          this.reorderEncounterLinesForEncounter({
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: arrayUpsert(
                entity.EncounterLineItems.LineItems,
                item.Code,
                {
                  LineType: CustomChargeCodeType.CSTM_CONSUMABLE,
                  LineNum: 99,
                  ChargeCode: '0197',
                  ChargeDesc: item.Description,
                  UnitPrice: item.UnitPrice,
                  TotalExcVat: 0,
                  TotalIncVat: 0,
                  Amount: (item.UnitPrice * item.PackSize) / 100,
                  PriceOverride: true,
                  ChargeQuan: item.PackSize,
                  Diagnosis: entity.EncounterLineItems.EncounterHeader.Diagnosis,
                } as EncounterLineItemVo,
                'NappiCode'
              ),
            },
          })
        );
      })
    );
  }

  calcMedicineQuantity(medicine: EncounterLineItemVo): number {
    /* if (dosageStaticForms.includes(medicineInfo?.hb_ProductUsageCategory?.toLowerCase())) {
      this.form.patchValue({
        Quantity: 1,
      });
    } else */
    if (dosageQuantityForms.includes(medicine.DosageType)) {
      return (
        getDosageQuantity(
          {
            DosageType: medicine.DosageType,
            DosageUnits: medicine.DosageUnit,
            PeriodType: medicine.PeriodType,
            PeriodUnits: medicine.PeriodUnit,
            FrequencyUnits: medicine.FrequencyUnit,
          } as DosageViewModel,
          medicine.DurationType,
          medicine.DurationUnit
        ) || 1
      );
    } else {
      return medicine.ChargeQuan || 1;
    }
  }

  calcQuantityForPrescriptionMedicine(medicine: MedicationPrescriptionLine): number {
    /* if (dosageStaticForms.includes(medicineInfo?.hb_ProductUsageCategory?.toLowerCase())) {
      this.form.patchValue({
        Quantity: 1,
      });
    } else */
    if (dosageQuantityForms.includes(medicine.DosageType)) {
      return (
        getDosageQuantity(
          {
            DosageType: medicine.DosageType,
            DosageUnits: medicine.DosageUnits,
            PeriodType: medicine.PeriodType,
            PeriodUnits: medicine.PeriodUnit,
            FrequencyUnits: medicine.FrequencyUnits,
          } as DosageViewModel,
          medicine.DurationType,
          medicine.DurationUnit
        ) || 1
      );
    } else {
      return medicine.Quantity || 1;
    }
  }

  updateProcedureOnEncounter(
    tenantId: string,
    patientId: string,
    procedureCode: string,
    lineNum: number,
    Amount: number,
    ChargeQuan: number
  ) {
    this.clinicalEncounterStore.update(patientId, entity =>
      this.reorderEncounterLinesForEncounter({
        ...entity,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          LineItems: arrayUpdate(
            entity.EncounterLineItems.LineItems,
            line => line.ChargeCode === procedureCode && line.LineNum === lineNum,
            {
              Amount,
              ChargeQuan,
            }
          ),
        },
      })
    );

    // let's re-price it now
    // this.priceEncounter(tenantId, patientId).subscribe();// no longer needed, is done in encounter summary
  }

  private getLinkedModifersForProcedure(encounterLines: EncounterLineItemVo[], procedureCode: string) {
    const lines = _.cloneDeep(encounterLines || []);
    const procedureLine = lines.find(line => line.LineType === 'Procedure' && line.ChargeCode === procedureCode);
    if (!procedureLine) {
      return [];
    }

    const modifierLines = lines
      .filter(line => line.LineNum > procedureLine.LineNum)
      .reduce(
        (a, line) => {
          if (a.complete) {
            return a;
          }
          if (line.LineType !== 'Modifier') {
            return { ...a, complete: true };
          }
          return { ...a, lines: arrayAdd(a.lines, line) };
        },
        { complete: false, lines: [] as EncounterLineItemVo[] }
      ).lines;

    return modifierLines;
  }

  removeAllLineItemsFromEncounter(tenantId: string, patientId: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => {
          return {
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: [],
            },
          };
        });
      })
    );
  }

  removeProcedureFromEncounter(tenantId: string, patientId: string, procedureCode: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => {
          const encounterLines = _.cloneDeep(entity.EncounterLineItems.LineItems);
          // list of linked modifiers for the procedure
          const linkedModifiers = this.getLinkedModifersForProcedure(encounterLines, procedureCode);

          // Filter out admin modifiers (0017) - these should be preserved
          const modifierLinesToBeRemoved = linkedModifiers
            .filter(line => !ADMIN_MODIFIER_CODES.includes(line.ChargeCode))
            .map(line => line.LineNum);

          // get new array of amended lines
          const amendedLines = arrayRemove(
            encounterLines,
            line =>
              ((line.LineType === 'Procedure' || line.LineType === CustomChargeCodeType.CSTM_PROCEDURE) &&
                line.ChargeCode === procedureCode) ||
              (line.LineType === 'Modifier' && modifierLinesToBeRemoved.includes(line.LineNum))
          );

          return {
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: this.reorderEncounterLines(amendedLines),
            },
          };
        });
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  removeAdminFromEncounter(tenantId: string, patientId: string, procedureCode: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => {
          const encounterLines = _.cloneDeep(entity.EncounterLineItems.LineItems);

          const amendedLines = arrayRemove(encounterLines, line => line.LineType === CustomChargeCodeType.ADMIN);

          return {
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: this.reorderEncounterLines(amendedLines),
            },
          };
        });
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  removeModifierFromEncounter(tenantId: string, patientId: string, modifierCode: string, lineNum: number) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => {
          const encounterLines = _.cloneDeep(entity.EncounterLineItems.LineItems);

          const amendedLines = arrayRemove(
            encounterLines,
            line => line.LineType === 'Modifier' && line.LineNum === lineNum && line.ChargeCode === modifierCode
          );

          return {
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: this.reorderEncounterLines(amendedLines),
            },
          };
        });
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  communicationLetter$(patientId: string, communicationId: string) {
    return this.encounterLettersQuery
      .selectEntity(patientId, e => e?.communications)
      .pipe(map(comm => comm?.find(d => d.Id === communicationId)));
  }

  clearCommunicationLetter(patientId: string, communicationId: string) {
    this.encounterLettersStore.upsert(patientId, entity => ({
      PatientId: patientId,
      communications: arrayRemove(entity.communications || [], communicationId, 'Id'),
    }));
  }

  updateLetterBody(patientId: string, letter: Partial<Letter>) {
    this.encounterLettersStore.upsert(patientId, entity => ({
      PatientId: patientId,
      communications: arrayUpsert(entity.communications || [], letter.Id, letter, 'Id'),
    }));
  }

  moveMedicineToOtherPrescription(tenantId: string, patientId: string, prescriptionId: string, prescriptionLineNumber: number) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      switchMap(encounter =>
        this.getPatientWeightForChild$(tenantId, patientId).pipe(map(patientWeight => ({ encounter, patientWeight })))
      ),
      map(({ encounter, patientWeight }) => {
        const prescriptions = encounter.MedicationsPrescriptions || [];
        const prescriptionSource = prescriptions.find(p => p.MedicationPrescriptionId === prescriptionId);
        if (!prescriptionSource) {
          return null;
        }
        return { encounter, prescriptionSource, patientWeight };
      }),
      filter(ret => !!ret),
      map(({ encounter, prescriptionSource, patientWeight }) => {
        const otherPrescription = (encounter.MedicationsPrescriptions || []).find(p => p.MedicationPrescriptionId !== prescriptionId);
        if (!otherPrescription) {
          const newPrescription = {
            MedicationPrescriptionId: uuidV4(),
            Patient: _.cloneDeep(encounter.PatientEventDetails.Patient),
            Provider: _.cloneDeep(encounter.Provider),
            IssuedDate: new Date(),
            FutureDate: null,
            PatientWeight: patientWeight || null,
            MedicationPrescriptionLines: [],
            Name: 'Prescription',
          } as MedicationPrescription;
          return {
            encounter,
            prescriptionSource,
            prescriptionDestination: newPrescription,
          };
        }
        return {
          encounter,
          prescriptionSource,
          prescriptionDestination: { ...otherPrescription, PatientWeight: patientWeight || otherPrescription.PatientWeight },
        };
      }),
      withTransaction(({ encounter, prescriptionSource, prescriptionDestination }) => {
        if (this.isSentToPharmacy(prescriptionDestination)) {
          throw new Error('Cannot move medicine to a prescription that has been sent to a pharmacy');
        }

        const lineToMove = _.cloneDeep(
          prescriptionSource.MedicationPrescriptionLines.find(line => line.LineNum === prescriptionLineNumber)
        );
        if (!lineToMove) {
          return;
        }

        const amendedSource: MedicationPrescription = {
          ..._.cloneDeep(prescriptionSource),
          // clear notes if prescription basically removed
          AdditionalNotes: prescriptionSource.MedicationPrescriptionLines.length == 1 ? null : prescriptionSource.AdditionalNotes || null,
          MedicationPrescriptionLines: this.reorderPrescriptionLines(
            arrayRemove(prescriptionSource.MedicationPrescriptionLines, line => line.LineNum === lineToMove.LineNum)
          ),
        };

        const amendedDestination: MedicationPrescription = {
          ..._.cloneDeep(prescriptionDestination),
          MedicationPrescriptionLines: this.reorderPrescriptionLines(
            arrayAdd(prescriptionDestination.MedicationPrescriptionLines, lineToMove)
          ),
        };

        const amendedPrescriptions = arrayUpsert(
          arrayUpsert(
            encounter.MedicationsPrescriptions,
            amendedDestination.MedicationPrescriptionId,
            amendedDestination,
            'MedicationPrescriptionId'
          ),
          amendedSource.MedicationPrescriptionId,
          amendedSource,
          'MedicationPrescriptionId'
        );

        if (amendedSource.MedicationPrescriptionLines.length === 0) {
          // remove related communication if prescription no longer contains any medicines
          this.removeClinicalCommunicationFromEncounter(tenantId, patientId, amendedSource.MedicationPrescriptionId);
        }

        this.clinicalEncounterStore.update(patientId, e => ({ MedicationsPrescriptions: amendedPrescriptions }));
        this.clearCommunicationLetter(patientId, amendedSource.MedicationPrescriptionId);
        this.clearCommunicationLetter(patientId, amendedDestination.MedicationPrescriptionId);
      }),
      switchMap(s => this.encounterInProgress$(tenantId, patientId))
    );
  }

  addMedicineToPrescription$(tenantId: string, patientId: string, prescriptionLine: MedicationPrescriptionLine) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      switchMap(encounter =>
        this.getPatientWeightForChild$(tenantId, patientId).pipe(map(patientWeight => ({ encounter, patientWeight })))
      ),
      tap(({ encounter, patientWeight }) => {
        // ensure we have created at least prescription created
        if (!encounter.MedicationsPrescriptions || encounter.MedicationsPrescriptions.length === 0) {
          this.clinicalEncounterStore.update(patientId, entity => ({
            MedicationsPrescriptions: [
              {
                MedicationPrescriptionId: uuidV4(),
                Patient: _.cloneDeep(entity.PatientEventDetails.Patient),
                Provider: _.cloneDeep(entity.Provider),
                IssuedDate: moment(new Date()).utc(true).toDate(),
                FutureDate: null,
                MedicationPrescriptionLines: [],
                PatientWeight: patientWeight || null,
                Name: 'Prescription',
              },
            ],
          }));
        } else if (
          encounter.MedicationsPrescriptions.length >= 1 &&
          encounter.MedicationsPrescriptions.every(s => this.isSentToPharmacy(s))
        ) {
          this.clinicalEncounterStore.update(patientId, entity => ({
            MedicationsPrescriptions: [
              ...entity.MedicationsPrescriptions,
              {
                MedicationPrescriptionId: uuidV4(),
                Patient: _.cloneDeep(entity.PatientEventDetails.Patient),
                Provider: _.cloneDeep(entity.Provider),
                IssuedDate: moment(new Date()).utc(true).toDate(),
                FutureDate: null,
                PatientWeight: patientWeight || null,
                MedicationPrescriptionLines: [],
                Name: 'Prescription',
              },
            ],
          }));
        }
      }),
      map(({ encounter }) => encounter),
      withTransaction(encounter => {
        this.clinicalEncounterStore.update(patientId, entity => {
          if ((entity.MedicationsPrescriptions || []).length > 0) {
            const firstPrescription = entity.MedicationsPrescriptions.find(p => !this.isSentToPharmacy(p));

            if (!firstPrescription) {
              throw new Error('Unable to add medicine: every prescription has already been sent to a pharmacy');
            }

            const updatedLines = this.reorderPrescriptionLines(
              arrayAdd(firstPrescription.MedicationPrescriptionLines, {
                ...prescriptionLine,
                MedicationDescription: getFriendlyMedicationDescription(prescriptionLine?.MedicationDescription),
                Parameters: {
                  ...prescriptionLine.Parameters,
                  DosageDescription:
                    prescriptionLine.Parameters?.DosageDescription ||
                    getFriendlyDosageDescription(
                      prescriptionLine.DosageUnits,
                      formDosageMeasurement(prescriptionLine.DosageType),
                      prescriptionLine.FrequencyUnits,
                      prescriptionLine.PeriodUnit,
                      prescriptionLine.PeriodType,
                      prescriptionLine.DurationUnit,
                      prescriptionLine.DurationType,
                      prescriptionLine?.Parameters?.RouteOfAdministration,
                      (prescriptionLine?.Parameters?.AdditionalInstructionSelections || '').includes('PRN')
                    ),
                },
                Diagnosis:
                  (prescriptionLine.Diagnosis?.length && prescriptionLine.Diagnosis) ||
                  encounter.EncounterLineItems.EncounterHeader.Diagnosis,
              })
            );

            const updatedPrescription: MedicationPrescription = {
              ...firstPrescription,
              MedicationPrescriptionLines: updatedLines,
            };
            const prescriptions = arrayUpdate(
              entity.MedicationsPrescriptions,
              firstPrescription.MedicationPrescriptionId,
              updatedPrescription,
              'MedicationPrescriptionId'
            );

            this.clearCommunicationLetter(patientId, updatedPrescription.MedicationPrescriptionId);
            return { MedicationsPrescriptions: prescriptions, MedicationsPrescribed: null } as EncounterVo;
          }
          return null;
        });
      })
    );
  }

  removeMedicineFromPrescription(tenantId: string, patientId: string, prescriptionId: string, lineNum: number) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      withTransaction(encounter => {
        const prescription = (encounter.MedicationsPrescriptions || []).find(p => p.MedicationPrescriptionId === prescriptionId);

        // find the prescription with the medicine
        if (prescription && (prescription.MedicationPrescriptionLines || []).some(line => line.LineNum === lineNum)) {
          // remove the medicine from the array
          const lines = this.reorderPrescriptionLines(
            arrayRemove(prescription.MedicationPrescriptionLines, row => row.LineNum === lineNum)
          );

          const updatedPrescription: MedicationPrescription = {
            ..._.cloneDeep(prescription),
            MedicationPrescriptionLines: lines,
          };

          const updatedPrescriptions: MedicationPrescription[] = arrayUpdate(
            encounter.MedicationsPrescriptions,
            prescription.MedicationPrescriptionId,
            updatedPrescription,
            'MedicationPrescriptionId'
          );

          if (lines.length === 0) {
            // remove related communication if prescription no longer contains any medicines
            this.removeClinicalCommunicationFromEncounter(tenantId, patientId, prescription.MedicationPrescriptionId);
            // remove the prescription if no longer contains any medicines (important for Dischem and future fresh prescription)
            this.clinicalEncounterStore.update(patientId, {
              MedicationsPrescriptions: arrayRemove(encounter.MedicationsPrescriptions, p => p.MedicationPrescriptionId === prescriptionId),
            });
          } else {
            // update the encounter with full prescriptions updated
            this.clinicalEncounterStore.update(patientId, { MedicationsPrescriptions: updatedPrescriptions });
          }

          this.clearCommunicationLetter(patientId, prescription.MedicationPrescriptionId);
        }
      })
    );
  }

  loadEncounterPrescription(practiceId: string, encounterId: string, prescriptionId: string) {
    return this.merakiEncounterService.getEncounterPrescriptionLive$(practiceId, encounterId, prescriptionId);
  }

  /*
   * Please note this can be used to upate prescription directly in firestore,
   * use only when enconter was completed, otherwise need to go via encounter update process (akita, saveEncounter, etc)
   */
  updateMedicationPrescriptionDischemStatus(practiceId, encounterId, prescriptionId, status: 'Sent' | 'Cancelled') {
    return this.loadEncounterPrescription(practiceId, encounterId, prescriptionId).pipe(
      take(1),
      switchMap(prescription =>
        this.merakiEncounterService.updateEncounterPrescriptionDischem(practiceId, encounterId, prescriptionId, {
          ...prescription,
          SentToDischem: { ...prescription.SentToDischem, Status: status },
        })
      )
    );
  }

  updateMedicationPrescription(
    tenantId: string,
    patientId: string,
    prescriptionId: string,
    medicationPrescription: Partial<MedicationPrescription>
  ) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const prescription = (entity.MedicationsPrescriptions || []).find(p => p.MedicationPrescriptionId === prescriptionId);

      const updatedPrescriptions: MedicationPrescription[] = arrayUpdate(
        entity.MedicationsPrescriptions,
        prescriptionId,
        { ...prescription, ...medicationPrescription },
        'MedicationPrescriptionId'
      );
      return { MedicationsPrescriptions: updatedPrescriptions };
    });
    this.clearCommunicationLetter(patientId, prescriptionId);
  }

  updateMedicineOnPrescription(
    tenantId: string,
    patientId: string,
    prescriptionId: string,
    lineNum: number,
    prescriptionLine: Partial<MedicationPrescriptionLine>
  ) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const prescription = (entity.MedicationsPrescriptions || []).find(p => p.MedicationPrescriptionId === prescriptionId);
      if (prescription?.MedicationPrescriptionLines?.length > 0) {
        const updatedLines = arrayUpdate(prescription.MedicationPrescriptionLines, lineNum, prescriptionLine, 'LineNum');
        const updatedPrescription: MedicationPrescription = {
          ...prescription,
          MedicationPrescriptionLines: updatedLines,
        };
        const updatedPrescriptions: MedicationPrescription[] = arrayUpdate(
          entity.MedicationsPrescriptions,
          prescriptionId,
          updatedPrescription,
          'MedicationPrescriptionId'
        );
        return { MedicationsPrescriptions: updatedPrescriptions };
      }
    });
    this.clearCommunicationLetter(patientId, prescriptionId);
  }

  addCustomCodeToEncounter$(encounter: EncounterVo, item: CustomChargeCodes): Observable<boolean> {
    if (item.Type == CustomChargeCodeType.CSTM_PROCEDURE) {
      return this.addCustomProcedureToEncounter$(encounter.PracticeId, encounter.PatientId, item).pipe(mapTo(true));
    } else if (item.Type == CustomChargeCodeType.CSTM_MEDICINE) {
      return this.addCustomMedicineToEncounter(encounter.PracticeId, encounter.PatientId, item).pipe(mapTo(true));
    } else if (item.Type == CustomChargeCodeType.CSTM_CONSUMABLE) {
      return this.addCustomConsumableToEncounter(encounter.PracticeId, encounter.PatientId, item).pipe(mapTo(true));
      /* } else if (item.Type == CustomChargeCodeType.ADMIN) { // not required for now, as we don't have admin tab
      return this.addCustomAdminToEncounter(encounter.PracticeId, encounter.PatientId, item).pipe(mapTo(true));
     */
    } else {
      return of(false);
    }
  }

  addCustomProcedureToEncounter$(tenantId: string, patientId: string, line: CustomChargeCodes) {
    // exclude default procedures from incrementing quantity
    return this.providerService.specialityRule$.pipe(
      take(1),
      tap(specialityRule => {
        const defaultProcedure = specialityRule.DefaultProcedureCode;
        this.clinicalEncounterStore.update(patientId, entity =>
          this.reorderEncounterLinesForEncounter({
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: this.arrayUpsertItem(
                entity.EncounterLineItems.LineItems,
                item => item.ChargeCode + item.LineType,
                {
                  LineType: CustomChargeCodeType.CSTM_PROCEDURE,
                  LineNum: 99,
                  ChargeCode: line.Code,
                  ChargeDesc: line.Description,
                  // ChargeQuan: line.Code !== defaultProcedure ? this.getEncounterLineQuantitySum(entity, line) : 1,
                  ChargeQuan: 1,
                  UnitPrice: line.UnitPrice,
                  TotalExcVat: 0,
                  TotalIncVat: 0,
                  Amount: line.UnitPrice / 100,
                  PriceOverride: true,
                  Diagnosis: entity.EncounterLineItems.EncounterHeader.Diagnosis,
                } as EncounterLineItemVo
                //'ChargeCode',
              ),
            },
          })
        );
      })
    );
  }

  addTemplateToEncounter$(encounter: EncounterVo, item: EncounterTemplateVo): Observable<boolean> {
    // todo as improvement apply akita transaction (currently due to rxjs nature it's hard to achieve)
    const diagnosisItems$ =
      item.EncounterLineItems?.EncounterHeader?.Diagnosis?.length > 0
        ? item.EncounterLineItems?.EncounterHeader?.Diagnosis?.map(diagnosis =>
            this.algoliaRestService.getAlgoliaDiagnosisToken(diagnosis.DiagnosisCode).pipe(
              map(DiagnosisToken => ({ ...diagnosis, DiagnosisToken }) as DiagnosisVo2),
              tap(diagnosisVo => this.upsertDiagnosisVoToEncounter(encounter.PracticeId, encounter.PatientId, diagnosisVo))
            )
          )
        : ([] as Observable<DiagnosisVo2>[]);

    const lineItems$ =
      item.EncounterLineItems?.LineItems?.length > 0
        ? item.EncounterLineItems?.LineItems?.map(line => this.addLineItemFromTemplate(line, encounter, item))
        : ([] as Observable<any>[]);

    const prescriptions$ =
      item.MedicationPrescriptionLines?.length > 0
        ? item.MedicationPrescriptionLines?.map(
            line => this.addMedicineToPrescription$(encounter.PracticeId, encounter.PatientId, line).pipe(mapTo(true))
            /* this.algoliaRestService.getMedicineInformationByNappi(line.NappiCode)
        .pipe(
          switchMap(medicineInfo => this.getDosages(encounter.PracticeId, medicineInfo.ATC5Code,
            medicineInfo.hbProductUsageCode, medicineInfo.ProductStrength)),
          switchMap(result => this.addMedicineToPrescription(encounter.PracticeId, encounter.PatientId, line)),
          mapTo(true)
        ) */
          )
        : ([] as Observable<boolean>[]);
    return concat(...diagnosisItems$, ...lineItems$, ...prescriptions$).pipe(
      toArray(), // allow to concat all obs results into one array and emit one value
      mapTo(true),
      catchError(() => of(false))
    );
  }

  private addLineItemFromTemplate(line: EncounterLineItemVo, encounter: EncounterStatusVo, item: EncounterTemplateVo): Observable<any> {
    {
      if (line.LineType === 'Procedure' || line.LineType === CustomChargeCodeType.CSTM_PROCEDURE) {
        return this.providerService.provider$.pipe(
          take(1),
          switchMap(provider =>
            line.LineType === CustomChargeCodeType.CSTM_PROCEDURE
              ? this.merakiLookupService
                  .getCustomChargeCodes(provider.PracticeNumber, CustomChargeCodeType.CSTM_PROCEDURE)
                  .pipe(map(s => s.find(f => f.Code === line.ChargeCode)))
              : of(null)
          ),
          switchMap(code =>
            this.addProcedureToEncounter(encounter.PracticeId, encounter.PatientId, null, {
              ...line,
              UnitPrice: code != null ? code.UnitPrice : line.UnitPrice,
              PriceOverride: code != null,
            })
          )
        );
      } else if (line.LineType === 'Medicine' || line.LineType === CustomChargeCodeType.CSTM_MEDICINE) {
        return line.Parameters == null || line.Parameters?.MedicineType === 'Dispense'
          ? this.providerService.provider$.pipe(
              take(1),
              switchMap(provider =>
                line.LineType === CustomChargeCodeType.CSTM_MEDICINE
                  ? this.merakiLookupService
                      .getCustomChargeCodes(provider.PracticeNumber, CustomChargeCodeType.CSTM_MEDICINE)
                      .pipe(map(s => s.find(f => f.Code === line.NappiCode)))
                  : of(null)
              ),
              tap(code =>
                this.addMedicineToEncounter(encounter.PracticeId, encounter.PatientId, {
                  ...line,
                  UnitPrice: code != null ? code.UnitPrice : line.UnitPrice,
                  PriceOverride: code != null,
                })
              )
            )
          : this.addMedicineToPrescription$(
              encounter.PracticeId,
              encounter.PatientId,
              convertMedicineToPrescriptionLine({
                ...line,
                ChargeQuan: this.calcMedicineQuantity(line),
              })
            );
      } else if (line.LineType === 'Modifier') {
        return of({}).pipe(
          tap(s => {
            const procedureCodes = [item.EncounterLineItems?.LineItems?.find(x => x.LineNum === line.LineNum - 1)?.ChargeCode];
            this.addModifierToProceduresOnEncounter(encounter.PracticeId, encounter.PatientId, line, procedureCodes);
          })
        );
      } else if (line.LineType === 'Consumable' || line.LineType === CustomChargeCodeType.CSTM_CONSUMABLE) {
        return this.providerService.provider$.pipe(
          take(1),
          switchMap(provider =>
            line.LineType === CustomChargeCodeType.CSTM_CONSUMABLE
              ? this.merakiLookupService
                  .getCustomChargeCodes(provider.PracticeNumber, CustomChargeCodeType.CSTM_CONSUMABLE)
                  .pipe(map(s => s.find(f => f.Code === line.NappiCode)))
              : of(null)
          ),
          switchMap(code =>
            this.addConsumableToEncounter(encounter.PracticeId, encounter.PatientId, {
              ...line,
              UnitPrice: code != null ? code.UnitPrice : line.UnitPrice,
              PriceOverride: code != null,
            })
          )
        );
      } else if (line.LineType === CustomChargeCodeType.ADMIN) {
        return this.providerService.provider$.pipe(
          take(1),
          switchMap(provider =>
            line.LineType === CustomChargeCodeType.ADMIN
              ? this.merakiLookupService
                  .getCustomChargeCodes(provider.PracticeNumber, CustomChargeCodeType.ADMIN)
                  .pipe(map(s => s.find(f => f.Code === line.ChargeCode)))
              : of(null)
          ),
          tap(code =>
            this.addAdminToEncounter(encounter.PracticeId, encounter.PatientId, {
              ...line,
              UnitPrice: code != null ? code.UnitPrice : line.UnitPrice,
              PriceOverride: code != null,
            })
          )
        );
      }
      return of();
    }
  }

  addConsumableToEncounter(
    tenantId: string,
    patientId: string,
    item: EncounterLineItemVo,
    categoryCode?: string,
    subCategoryCode?: string
  ) {
    return this.providerService.specialityRule$.pipe(
      take(1),
      tap({
        next: specialityRule => {
          this.clinicalEncounterStore.update(patientId, entity => {
            let lineItems = entity.EncounterLineItems.LineItems;
            const line = entity.EncounterLineItems.LineItems.find(s => s.NappiCode === item.NappiCode);
            if (line != null) {
              lineItems = arrayUpdate(entity.EncounterLineItems.LineItems, l => l.NappiCode === item.NappiCode, {
                ...line,
                ChargeQuan: line.ChargeQuan + 1,
                Amount: item.LineType == CustomChargeCodeType.CSTM_CONSUMABLE ? (line.UnitPrice * (line.ChargeQuan + 1)) / 100 : null,
              });
            } else {
              lineItems = arrayAdd(entity.EncounterLineItems.LineItems, {
                LineType: item.LineType,
                LineNum: 99, // add to end
                ChargeCode: specialityRule.ConsumableProcedureCode,
                ChargeDesc: item.ChargeDesc,
                NappiCode: item.NappiCode,
                ChargeQuan: item.ChargeQuan,
                UnitPrice: item.UnitPrice,
                TotalExcVat: null,
                TotalIncVat: null,
                Amount: item.LineType == CustomChargeCodeType.CSTM_CONSUMABLE ? (item.UnitPrice * item.ChargeQuan) / 100 : null,
                PriceOverride: item.PriceOverride,
                Diagnosis: entity.EncounterLineItems.EncounterHeader.Diagnosis,
                Parameters: {
                  CategoryCode: categoryCode,
                  SubCategoryCode: subCategoryCode,
                  Token: `C-${specialityRule.ConsumableProcedureCode}-${subCategoryCode}`,
                },
              } as EncounterLineItemVo);
            }

            return this.reorderEncounterLinesForEncounter({
              ...entity,
              EncounterLineItems: {
                ...entity.EncounterLineItems,
                LineItems: lineItems,
              },
            });
          });
        },
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  updateMedicalInsurance(patientId: string, medicalInsurance: MedicalInsuranceVo) {
    this.clinicalEncounterStore.update(patientId, entity => ({ MedicalInsurance: medicalInsurance }));
  }

  updateConsumableOnEncounter(
    tenantId: string,
    patientId: string,
    diagnosisToken: string,
    code: string,
    quantity: number,
    amount: number,
    priceOverride: boolean
  ) {
    return of(null).pipe(
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity =>
          this.reorderEncounterLinesForEncounter({
            ...entity,
            EncounterLineItems: {
              ...entity.EncounterLineItems,
              LineItems: arrayUpdate(entity.EncounterLineItems.LineItems, e => e.NappiCode === code, {
                ChargeQuan: quantity,
                Amount: amount,
                PriceOverride: priceOverride,
              } as EncounterLineItemVo),
            },
          })
        );
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  removeConsumableFromEncounter(tenantId: string, patientId: string, code: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => ({
          ...entity,
          EncounterLineItems: {
            ...entity.EncounterLineItems,
            LineItems: this.reorderEncounterLines(
              arrayRemove(
                entity.EncounterLineItems.LineItems,
                line => (line.LineType === 'Consumable' || line.LineType == CustomChargeCodeType.CSTM_CONSUMABLE) && line.NappiCode === code
              )
            ),
          },
        }));
      })
      // switchMap(() => this.priceEncounter(tenantId, patientId))// no longer needed, is done in encounter summary
    );
  }

  removeMedicineFromEncounter(tenantId: string, patientId: string, code: string, lineNum: number) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      tap(() => {
        this.clinicalEncounterStore.update(patientId, entity => ({
          ...entity,
          EncounterLineItems: {
            ...entity.EncounterLineItems,
            LineItems: this.reorderEncounterLines(
              arrayRemove(
                entity.EncounterLineItems.LineItems,
                line =>
                  (line.LineType === 'Medicine' || line.LineType == CustomChargeCodeType.CSTM_MEDICINE) &&
                  line.NappiCode === code &&
                  line.LineNum === lineNum
              )
            ),
          },
        }));
      })
    );
  }

  addToEncounter(tenantId: string, patientId: string, diagnosisToken: string, code: string, description: string, type: string) {
    this.clinicalEncounterStore.update(patientId, entity =>
      this.reorderEncounterLinesForEncounter({
        ...entity,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          LineItems: arrayAdd(entity.EncounterLineItems.LineItems, {
            LineType: type,
            ChargeCode: code,
            ChargeDesc: description,
            ChargeQuan: 1,
            UnitPrice: null,
            TotalExcVat: null,
            TotalIncVat: null,
            Amount: null,
            PriceOverride: false,
          } as EncounterLineItemVo),
        },
      })
    );

    // let's price it now
    // this.priceEncounter(tenantId, patientId).subscribe();// no longer needed, is done in encounter summary
  }

  upadateToEncounter(tenantId: string, patientId: string, diagnosisToken: string, item: EncounterLineItemVo) {
    this.clinicalEncounterStore.update(patientId, entity =>
      this.reorderEncounterLinesForEncounter({
        ...entity,
        EncounterLineItems: {
          ...entity.EncounterLineItems,
          LineItems: arrayAdd(entity.EncounterLineItems.LineItems, {
            LineType: item.LineType,
            ChargeCode: item.ChargeCode,
            ChargeDesc: item.ChargeDesc,
            ChargeQuan: item.ChargeQuan,
            UnitPrice: 0,
            TotalExcVat: 0,
            TotalIncVat: 0,
            PriceOverride: false,
          } as EncounterLineItemVo),
        },
      })
    );

    // let's price it now
    // this.priceEncounter(tenantId, patientId).subscribe();// no longer needed, is done in encounter summary
  }

  repeatScript(tenantId: string, patientId: string, medication: MedicationPrescription) {
    // return this.clinicalNewClient.addPatientPrescription(tenantId, patientId, medication);
    return this.providerService.communicationTemplates$.pipe(
      take(1),
      map(templates => templates.find(x => x.TemplateIdentifier === 'Prescription' && x.Locale === 'EN')),
      switchMap(template =>
        this.clinicalNewClient
          .renderAdhocPrescriptionPOST(tenantId, patientId, medication.MedicationPrescriptionId, {
            MedicationPrescription: medication,
            Template: template,
          })
          .pipe(
            switchMap(result => {
              if (!result.Sucess) {
                const msg = result.ResponseMessage || 'Failed to render prescription';
                this.appInsightsService.trackException(new Error(msg), 'render-adhocscript', { patientId: patientId });
                return throwError(new Error(msg));
              }
              return of(result);
            })
          )
      ),
      withLatestFrom(this.providerService.provider$),
      // 1. save html to storage
      switchMap(([content, provider]) =>
        this.merakiService
          .updateHtmlToStorage(provider.PracticeNumber, 'Prescriptions', medication.MedicationPrescriptionId, content.Data)
          .pipe(
            switchMap(result => {
              if (!result.Success) {
                const msg = (result as any).Error.message || 'Failed to upload prescription HTML to storage';
                this.appInsightsService.trackException(new Error(msg), 'save-adhocscript', { patientId: patientId });
                return throwError(new Error(msg));
              }
              return of(content);
            })
          )
      ),
      // 2. add to timeline
      switchMap(content =>
        this.clinicalNewClient
          .addPatientTimelineEntries(
            tenantId,
            patientId,
            medication.MedicationPrescriptionLines.map(l => ({
              Type: TimelineEventType.AdhocPrescriptionLineType,
              ParentReferenceId: medication.MedicationPrescriptionId,
              Code: l.NappiCode,
              Description: medication.MedicationPrescriptionId,
              DateTime: medication.IssuedDate,
              Narrative: `℞: ${l.Description}`,
              PatientId: patientId,
              ParentLineNum: 0,
            }))
          )
          .pipe(mapTo(content))
      )
    );
  }

  capturePatientClinicalMetrics(tenantId: string, patientId: string, metrics: ClinicalMetricVo[], editScreening: boolean = false) {
    // return this.clinicalPatientClient.capturePatientClinicalMetrics(tenantId, patientId, metrics);
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider =>
        this.patientsService.patientCapturedMetricsById$(provider.PracticeId, patientId).pipe(
          take(1),
          switchMap(allMetrics =>
            this.merakiPatientService.saveClinicalMetrics(provider.PracticeTenantId, patientId, allMetrics, metrics, editScreening)
          ),
          // update patient timeline with clinical metrics
          // todo confirm what to do when delete
          switchMap(result => {
            var entries = result.map((c, index) => this.patientsService.clinicalMetricToTimelineEntry(c, index, patientId, provider));
            if (editScreening) {
              // if editing screening, we need don't delete old items, just add new labeled (updated)
              var guid = uuidV4();
              entries = entries.map(s => ({
                ...s,
                ParentReferenceId: guid,
                Description: s.Description + ' (updated)',
                Narrative: s.Narrative + ' (updated)',
              }));
            }
            return entries.length > 0
              ? this.clinicalNewClient
                  .addPatientTimelineEntries(provider.PracticeId, patientId, entries)
                  .pipe(switchMap(s => this.patientsService.loadPatientCapturedMetrics(patientId)))
              : of(null);
          })
        )
      ),
      mapTo({ Sucess: true } as RestApiResultOfString),
      catchError(s => of({ Sucess: false, ResponseMessage: 'Failed to save metrics' } as RestApiResultOfString))
    );
  }

  removeBabyPatientClinicalMetrics(patientId: string, referenceId: string) {
    // return this.clinicalPatientClient.removePatientClinicalMetrics(tenantId, patientId, metrics);
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider =>
        this.patientsService.patientCapturedMetricsById$(provider.PracticeId, patientId).pipe(
          take(1),
          switchMap(allMetrics =>
            this.merakiPatientService
              .removePatientClinicalMetrics(provider.PracticeTenantId, patientId, allMetrics, referenceId, babyMetricTypes)
              .pipe(s => {
                const metricsForDelete = _.chain(allMetrics)
                  .filter(s => s.ReferenceId == referenceId && babyMetricTypes.includes(s.Name))
                  .map((s, index) => this.patientsService.clinicalMetricToTimelineEntry(s, index, patientId, provider))
                  .value();
                return this.clinicalNewClient.removePatientTimelineEntries(provider.PracticeId, patientId, metricsForDelete);
              })
          )
        )
      )
    );
  }

  getAllPatientClinicalMetrics(tenantId: string, patientId: string) {
    // return this.clinicalPatientClient.getAllPatientClinicalMetrics(tenantId, patientId);
    return this.patientsService.loadPatientCapturedMetrics(patientId);
  }

  removeLineFromEncounter(tenantId: string, patientId: string, lineNum: number) {
    const encounterLineType = (this.clinicalEncounterQuery.getEntity(patientId).EncounterLineItems.LineItems || []).find(
      line => line.LineNum === lineNum
    );

    if (!encounterLineType) {
      return EMPTY;
    }

    switch (encounterLineType.LineType) {
      case 'Procedure':
      case CustomChargeCodeType.CSTM_PROCEDURE:
        return this.removeProcedureFromEncounter(tenantId, patientId, encounterLineType.ChargeCode);
      case 'Consumable':
      case CustomChargeCodeType.CSTM_CONSUMABLE:
        return this.removeConsumableFromEncounter(tenantId, patientId, encounterLineType.NappiCode);
      case 'Modifier':
        return this.removeModifierFromEncounter(tenantId, patientId, encounterLineType.ChargeCode, encounterLineType.LineNum);
      case 'Medicine':
      case CustomChargeCodeType.CSTM_MEDICINE:
        return this.removeMedicineFromEncounter(tenantId, patientId, encounterLineType.NappiCode, lineNum);
      case CustomChargeCodeType.ADMIN:
        return this.removeAdminFromEncounter(tenantId, patientId, encounterLineType.ChargeCode);

      default:
        throw new Error('Unknown line type');
    }
  }

  validateSickNote(s: MedicalCertificate) {
    return !!s.FromDate && !!s.ToDate && (!s.Accompanied || (!!s.AccompaniedFromDate && !!s.AccompaniedToDate && !!s.AccompaniedBy));
  }

  sickNoteRequireRefresh$(tenantId, patientId): Observable<boolean> {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      map(encounter => encounter.MedicalCertificate),
      take(1),
      filter(s => this.validateSickNote(s)), // used as validation if something is missing
      withLatestFrom(this.patientsService.patientUIState$(patientId).pipe(map(state => state.prevSickNote))),
      map(
        ([sickNoteDetails, prevSickNote]) =>
          !prevSickNote?.htmlBody ||
          !_.isEqual(
            sickNoteDetails?.Diagnoses.map(d => d.DiagnosisCode),
            prevSickNote?.medicalCertificate?.Diagnoses.map(d => d.DiagnosisCode)
          ) ||
          !_.isEqual(sickNoteDetails?.FromDate.toDateString(), prevSickNote?.medicalCertificate?.FromDate?.toDateString()) ||
          !_.isEqual(sickNoteDetails?.ToDate.toDateString(), prevSickNote?.medicalCertificate?.ToDate?.toDateString()) ||
          !_.isEqual(sickNoteDetails?.ReturnDate?.toDateString(), prevSickNote?.medicalCertificate?.ReturnDate?.toDateString()) ||
          !_.isEqual(sickNoteDetails?.IncludeSignature, prevSickNote?.medicalCertificate?.IncludeSignature) ||
          !_.isEqual(sickNoteDetails?.IncludeTimestamp, prevSickNote?.medicalCertificate?.IncludeTimestamp) ||
          !_.isEqual(sickNoteDetails?.AsInformed, prevSickNote?.medicalCertificate?.AsInformed) ||
          !_.isEqual(sickNoteDetails?.TelephonicConsultation, prevSickNote?.medicalCertificate?.TelephonicConsultation) ||
          !_.isEqual(sickNoteDetails?.AccompaniedBy, prevSickNote?.medicalCertificate?.AccompaniedBy) ||
          !_.isEqual(
            sickNoteDetails?.AccompaniedFromDate?.toDateString(),
            prevSickNote?.medicalCertificate?.AccompaniedFromDate?.toDateString()
          ) ||
          !_.isEqual(
            sickNoteDetails?.AccompaniedToDate?.toDateString(),
            prevSickNote?.medicalCertificate?.AccompaniedToDate?.toDateString()
          ) ||
          !_.isEqual(sickNoteDetails?.FollowupDate?.toDateString(), prevSickNote?.medicalCertificate?.FollowupDate?.toDateString())
      )
    );
  }

  generateMedicalCertificate$(tenantId: string, patientId: string): Observable<string> {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      switchMap(encounter =>
        this.createCommunication(
          encounter.PatientEventDetails.PracticeId,
          encounter.PatientEventDetails.Patient.PatientId,
          encounter.MedicalCertificate.MedicalCertificateId,
          encounter
        ).pipe(map(result => ({ encounter, result })))
      ),
      map(data => {
        this.patientsService.updateSickNoteUIState(
          data.encounter.PatientEventDetails.Patient.PatientId,
          data.encounter.MedicalCertificate,
          data.result
        );
        return data.result;
      })
    );
  }

  updateCommunicationsBranch(tenantId: string, patientId: string): Observable<any[]> {
    // todo refactor to take into account new prescription logic
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      switchMap(encounter => {
        // don't check prescriptions, because it has own regeneration logic, even for branch changes
        const generateSickNote = !!encounter.MedicalCertificate?.ToDate;
        const generateReferrals = encounter.Communications.filter(x => x.Type === 'Referrals').length > 0;
        const communications$ = [];

        if (generateSickNote) {
          communications$.push(
            this.createCommunication(
              encounter.PatientEventDetails.PracticeId,
              encounter.PatientEventDetails.Patient.PatientId,
              encounter.MedicalCertificate.MedicalCertificateId,
              encounter
            ).pipe(
              tap(res =>
                this.patientsService.updateSickNoteUIState(
                  encounter.PatientEventDetails.Patient.PatientId,
                  encounter.MedicalCertificate,
                  res
                )
              )
            )
          );
        }

        if (generateReferrals) {
          encounter.Communications.filter(x => x.Type === 'Referrals').forEach(referral => {
            communications$.push(
              this.createCommunication(
                encounter.PatientEventDetails.PracticeId,
                encounter.PatientEventDetails.Patient.PatientId,
                referral.CommunicationId,
                encounter
              )
            );
          });
        }
        return communications$.length > 0 ? forkJoin(communications$) : of([]);
      })
    );
  }

  getSickNoteHtmlUi(tenantId: string, patientId: string): Observable<string> {
    return this.patientsService.patientUIState$(patientId).pipe(
      map(state => state.prevSickNote?.htmlBody),
      take(1)
    );
  }

  upsetMedicalCertificateToEncounter(tenantId: string, patientId: string, medicalCertificate: MedicalCertificate) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      MedicalCertificate: medicalCertificate,
    }));
  }

  addMedicationPrescription(tenantId: string, patientId: string, medicationPrescription: MedicationPrescription) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      MedicationsPrescriptions: arrayAdd(entity.MedicationsPrescriptions, medicationPrescription),
    }));
  }

  createCommunication(tenantId: string, patientId: string, communicationId: string, encounter: EncounterVo, embededAiBody?: string) {
    const encounterUpdated = this.removeDosageInstructionFromEncounter(encounter);
    const templateId = encounter.Communications.find(x => x.CommunicationId === communicationId).CommunicationTemplateId;

    return this.providerService.getPracticeBranches(tenantId).pipe(
      map(branches => branches.find(b => b.BranchXRef == encounter.BranchXRef)),
      withLatestFrom(this.providerService.communicationTemplates$),
      take(1),
      map(([branch, templates]) => ({
        branch: branch,
        template: templates.find(x => x.TemplateId === templateId),
        baseTemplate: templates.find(s => s.TemplateIdentifier === 'CommonTemplate'),
      })),
      switchMap(({ branch, template, baseTemplate }) => {
        // depending on report type load extra information
        if (template.TemplateIdentifier === 'Prescription') {
          return this.patientsService
            .getPatientAllergiesMedications$(tenantId, patientId)
            .pipe(map(allergies => ({ Template: template, PatientAllergies: allergies, Branch: branch })));
        }

        if (template.TemplateType == 'custom-letter') {
          return this.patientsService.getPatientAllergiesMedications$(tenantId, patientId).pipe(
            map(allergies => ({
              Template: template,
              BaseTemplate: baseTemplate,
              PatientAllergies: allergies,
              Branch: branch,
              Encounter: encounterUpdated,
            })),
            map(request => this.setCustomLetterPlaceholders(request))
          );
        }

        if (template.TemplateType == 'Referrals') {
          return forkJoin([
            of({ Template: template, Branch: branch }),
            this.patientsService.getPatientClinicalNotesById$(tenantId, patientId).pipe(
              take(1),
              map(s => ({ ClinicalNote: s?.Note }))
            ),
            this.patientsService.patientAllergiesById$(tenantId, patientId).pipe(
              take(1),
              map(s => ({
                PatientAllergies: s.PatientAllergies.map(d => d.Allergen.Description),
                PatientAllergiesNote: s.Other,
              }))
            ),
            this.patientsService.patientConditionsById$(tenantId, patientId).pipe(
              take(1),
              map(s => ({ ChronicConditions: s.Conditions?.map(s => s.Condition.Name) }))
            ),
            this.patientsService.getPatientSurgicalHistory$(tenantId, patientId).pipe(
              take(1),
              map(s => ({ SurgicalHistory: s }))
            ),
          ]).pipe(map(items => Object.assign({}, ...items)));
        }
        if (template.TemplateId == baseTemplate.TemplateId && embededAiBody) {
          // for custom letters with no base template (AI generated letters)
          return of({
            Template: this.customLetterTemplateWithSignature(template),
            BaseTemplate: baseTemplate,
            Branch: branch,
          });
        }

        return of({ Template: template, BaseTemplate: baseTemplate, Branch: branch });
      }),
      switchMap(request =>
        this.clinicalNewClient
          .renderEncounterCommunicationPOST(tenantId, patientId, communicationId, {
            ...request,
            Encounter: encounterUpdated,
          })
          .pipe(
            map(result => {
              if (!result.Sucess) {
                throw new Error('Failed to render encounter communication');
              }
              return embededAiBody ? this.replaceLetterBody(result.Data, embededAiBody) : result.Data;
            })
          )
      ),
      switchMap(data => this.updateCommunication(encounter.EncounterId, communicationId, { Content: data }).pipe(mapTo(data)))
    );
  }
  // Render Adhoc AI generated communication (e.g., referral letter, custom letter) and update the communication content
  renderAdhocAiCommunication(tenantId: string, patientId: string, communicationId: string, templateId: string, embededAiBody?: string) {
    return this.providerService.getPracticeBranches(tenantId).pipe(
      take(1),
      withLatestFrom(
        this.patientsService.patientDetailsById$(tenantId, patientId),
        this.providerService.communicationTemplates$,
        this.providerService.provider$
      ),
      map(([branches, patient, templates, provider]) => {
        const branch = branches.find(b => b.IsMainBranch) || branches[0];
        const encounter = {
          EncounterId: communicationId,
          PatientId: patientId,
          BranchXRef: branch?.BranchXRef,
          PatientEventDetails: {
            PracticeId: tenantId,
            Patient: patient,
          },
          Provider: this.mapProviderConfiguration(provider),
          Communications: [
            {
              CommunicationId: communicationId,
              CommunicationTemplateId: templateId,
            },
          ],
          EncounterLineItems: {
            EncounterHeader: {
              DateOfService: new Date(),
            },
            LineItems: [],
          },
          MedicationsPrescriptions: [],
        } as EncounterStatusVo;

        return {
          encounter,
          branch,
          template: templates.find(x => x.TemplateId === templateId),
          baseTemplate: templates.find(s => s.TemplateIdentifier === 'CommonTemplate'),
        };
      }),
      switchMap(({ encounter, branch, template, baseTemplate }) => {
        const request =
          template.TemplateId == baseTemplate.TemplateId && embededAiBody
            ? {
                Template: this.customLetterTemplateWithSignature(template),
                BaseTemplate: baseTemplate,
                Branch: branch,
                Encounter: encounter,
              }
            : { Template: template, BaseTemplate: baseTemplate, Branch: branch, Encounter: encounter };

        return this.clinicalNewClient.renderEncounterCommunicationPOST(tenantId, patientId, communicationId, request).pipe(
          map(result => {
            if (!result.Sucess) {
              throw new Error('Failed to render encounter communication');
            }
            return embededAiBody ? this.replaceLetterBody(result.Data, embededAiBody) : result.Data;
          }),
          switchMap(content =>
            this.updateCommunication(undefined, communicationId, { Content: content }).pipe(map(() => ({ content, encounter })))
          )
        );
      })
    );
  }

  private customLetterTemplateWithSignature(template: CommunicationTemplateVo) {
    return {
      ...template,
      TemplateBody: '<template_body/>\n{{ProviderSignature}}',
      TemplateType: 'custom-letter',
    } as CommunicationTemplateVo;
  }

  replaceLetterBody(template: string, alteredBody: string): string {
    const aiLetter = template
      .replace('--AIBodyContent--', alteredBody) // for ai referral letter case
      .replace('<template_body/>', alteredBody); // for ai custom letter cases

    return aiLetter;
  }

  setCustomLetterPlaceholders(request: CommunicationRenderRequest): CommunicationRenderRequest {
    // please note done here to skip updating florence backend, older placeholders still filled on backend
    // see full list of possible placeholders here ReferenceDataService.SpecialPlaceholders
    let body = request.Template.TemplateBody;
    body = body.replace(
      new RegExp('{{PatientEmailAddress}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientDetails.EmailAddress ?? ''
    );
    body = body.replace(
      new RegExp('{{PatientMobileNumber}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientDetails.ContactNo ?? ''
    );
    body = body.replace(
      new RegExp('{{MembershipNumber}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidMembershipNumber ?? ''
    );
    body = body.replace(
      new RegExp('{{AccountNo}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientAccountDetails.AccountNo ?? ''
    );
    body = body.replace(
      new RegExp('{{MedicalAidName}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidName ?? ''
    );
    body = body.replace(
      new RegExp('{{MedicalAidPlan}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidPlan ?? ''
    );
    body = body.replace(
      new RegExp('{{MedicalAidOption}}', 'g'),
      request.Encounter.PatientEventDetails.Patient.PatientAccountDetails.MedicalAidPlanOption ?? ''
    );
    body = body.replace(new RegExp('{{PatientAllergies}}', 'g'), request.PatientAllergies.join(', '));
    body = body.replace(
      new RegExp('{{ICD10Codes}}', 'g'),
      request.Encounter.EncounterLineItems.EncounterHeader.Diagnosis.map(s => s.DiagnosisCode).join(', ')
    );
    body = body.replace(
      new RegExp('{{ReportDate}}', 'g'),
      moment(/* request.Encounter.EncounterLineItems.EncounterHeader.DateOfService */).format('YYYY-MM-DD')
    );
    body = body.replace(
      new RegExp('{{PatientDOB}}', 'g'),
      moment(request.Encounter.PatientEventDetails.Patient.PatientDetails.DateOfBirth).format('DD-MMM-yyyy')
    );
    body = body.replace(new RegExp('{{FileNumber}}', 'g'), request.Encounter.PatientEventDetails.Patient.FileNo ?? '');
    body = body.replace(new RegExp('{{Occupation}}', 'g'), request.Encounter.PatientEventDetails.Patient.Occupation ?? '');
    body = body.replace(new RegExp('{{Employer}}', 'g'), request.Encounter.PatientEventDetails.Patient.Employer ?? '');
    body = body.replace(new RegExp('{{Address1}}', 'g'), request.Encounter.PatientEventDetails.Patient.PhysicalAddress?.Line1 ?? '');
    body = body.replace(new RegExp('{{Address2}}', 'g'), request.Encounter.PatientEventDetails.Patient.PhysicalAddress?.Line2 ?? '');
    body = body.replace(new RegExp('{{Address3}}', 'g'), request.Encounter.PatientEventDetails.Patient.PhysicalAddress?.Line3 ?? '');
    body = body.replace(new RegExp('{{PostalCode}}', 'g'), request.Encounter.PatientEventDetails.Patient.PhysicalAddress?.Code ?? '');
    return { ...request, Template: { ...request.Template, TemplateBody: body } };
  }

  updateClinicalCommunicationToEncounter(tenantId: string, patientId: string, communication: CommunicationVo) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      Communications: arrayUpdate(
        encounter.Communications,
        entity => entity.CommunicationId === communication.CommunicationId,
        communication
      ),
    }));
  }

  getPatientWeightForChild$(tenantId: string, patientId: string) {
    return this.patientsService.patientById$(patientId).pipe(
      take(1),
      switchMap(patient => {
        //  check if less than or equal 12 years old
        if (moment.duration(moment().diff(patient.details.PatientDetails.DateOfBirth)).asYears() <= 12) {
          return this.patientsService.getPatientDashboardOrLoad$(tenantId, patientId).pipe(
            map(dashboard => dashboard?.PatientDashboardEvents || []),
            withLatestFrom(this.encounterInProgressOrNulll$(tenantId, patientId)),
            map(([events, encounter]) => {
              const encounterEvents = encounter
                ? encounter.ClinicalMetrics.map(s => this.patientsService.clinicalMetricToTimelineEntry(s, 0, patientId, {}))
                : [];

              const weights = _.chain(events.map(e => ({ ...e, DateTime: moment(e.DateTime).toDate() }) as PatientDashboardHistoryEventDto))
                .concat(encounterEvents)
                .filter(x => x.Type === 'ClinicalMetric:Weight')
                .orderBy(x => x.DateTime, 'desc');

              return weights.first().value();
            })
          );
        }
        return of(null);
      })
    );
  }

  updateQuestionnaireToEncounter(patientId: string, questionnaire: QuestionnaireVo) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      Questionnaires: arrayUpsert(encounter.Questionnaires || [], questionnaire.Id, questionnaire),
    }));
  }

  deleteQuestionnaireFromEncounter(patientId: string, questionnaireId: string) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      Questionnaires: arrayRemove(encounter.Questionnaires || [], questionnaireId),
    }));
  }

  @transaction() // update encounter in one transaction to minimize no. of separate emissions
  upsertCommunicationPrescriptionToEncounter(
    templates: CommunicationTemplateVo[],
    encounter: EncounterVo,
    medicationPrescriptionId: string,
    includeSignature: boolean,
    IsOriginalScript: boolean,
    excludeDate: boolean,
    additionalNotes: string
  ): string {
    const template = templates.find(t => t.TemplateIdentifier === 'Prescription' && t.PracticeId === encounter.PracticeId);
    const templateId = !template ? templates.find(t => t.TemplateIdentifier === 'Prescription').TemplateId : template.TemplateId;

    const prescriptionPosition = encounter.MedicationsPrescriptions.findIndex(s => s.MedicationPrescriptionId === medicationPrescriptionId);
    const newCommunication = {
      CommunicationId: medicationPrescriptionId,
      PatientId: encounter.PatientEventDetails.Patient.PatientId,
      EncounterId: encounter.EncounterId,
      CommunicationTemplateId: templateId,
      IncludeSignature: includeSignature,
      Type: 'Prescription',
      Title: `Prescription ${prescriptionPosition + 1}`,
    } as CommunicationVo;

    this.updateMedicationPrescription(encounter.PracticeId, newCommunication.PatientId, medicationPrescriptionId, {
      AdditionalNotes: additionalNotes || null,
      ExcludeReportDate: excludeDate || null,
      IsOriginalScript: IsOriginalScript || false,
    });

    this.upsertClinicalCommunicationToEncounter(encounter.PatientEventDetails.PracticeId, newCommunication.PatientId, newCommunication);
    return newCommunication.CommunicationId;
  }

  /**
   * Sync actual prescriptions with communication report
   */
  syncPrescriptionCommunications$(tenantId: string, patientId: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      withLatestFrom(this.providerService.communicationTemplates$, this.providerService.ExcludeSignatureFromPrescriptions$),
      tap(([encounter, templates, signature]) =>
        encounter.MedicationsPrescriptions?.filter(p => p.MedicationPrescriptionLines?.length > 0).forEach(prescription => {
          const communication = encounter.Communications.find(com => com.CommunicationId === prescription.MedicationPrescriptionId);
          if (!communication) {
            this.upsertCommunicationPrescriptionToEncounter(
              templates,
              encounter,
              prescription.MedicationPrescriptionId,
              !signature,
              prescription.IsOriginalScript,
              prescription.ExcludeReportDate,
              prescription.AdditionalNotes
            );
          }
        })
      )
    );
  }

  /**
   * Generate prescriptions reports, heavy call to server, use wisely
   */
  generatePrescriptionReport$(tenantId: string, patientId: string): Observable<string[]> {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      take(1),
      switchMap(encounter => {
        const prescriptions = encounter.Communications.filter(c => c.Type === 'Prescription');
        return prescriptions.length > 0
          ? forkJoin(
              prescriptions.map((communication, index) =>
                this.communicationLetter$(encounter.PatientId, communication.CommunicationId).pipe(
                  map(letter => ({ letter, id: communication.CommunicationId })),
                  take(1),
                  switchMap(
                    response =>
                      (!response.letter?.BodyHtml &&
                        this.createCommunication(encounter.PatientEventDetails.PracticeId, encounter.PatientId, response.id, encounter)) ||
                      of(response.letter.BodyHtml)
                  ),
                  tap(html =>
                    this.updateLetterBody(encounter.PatientId, {
                      Id: communication.CommunicationId,
                      BodyHtml: html,
                    })
                  ),
                  // trick to avoid concurrent issue (happens if we have 2 prescriptions and trying to create them in parallel)
                  delay(index * 10)
                )
              )
            )
          : of([]);
      })
    );
  }

  getRenderReferralTemplate(practiceId: string, encounterId: string, communicationId: string) {
    // return this.clinicalClient.getRenderReferralTemplate(practiceId, encounterId, templateId, 0);
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider => this.getEncounterCommunicationBody(provider.PracticeNumber, communicationId))
    );
  }

  getEncounterCommunicationBody(bpn: string, communicationId: string) {
    return this.merakiService.getHtmlBodyFromStorage(bpn, 'EncounterCommunications', communicationId).pipe(
      catchError(s => of(null)),
      switchMap(s => (!!s ? of(s) : this.merakiService.getHtmlBodyFromStorage('unknown', 'EncounterCommunications', communicationId)))
    );
  }

  removeMedicationPrescription(tenantId: string, patientId: string, medicationPrescriptionId: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      MedicationsPrescriptions: arrayRemove(entity.MedicationsPrescriptions, medicationPrescriptionId, 'MedicationPrescriptionId'),
    }));
  }

  updateMedicationPrescriptions(tenantId: string, patientId: string, medicationPrescriptions: MedicationPrescription[]) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      MedicationsPrescriptions: medicationPrescriptions,
    }));
  }

  updatePlanOutcomesToEncounter(practiceId: string, patientId: string, planOutcomes: EncounterPlanOutcomeVo[]) {
    this.clinicalEncounterStore.update(patientId, entity => ({ EncounterPlanOutcomes: planOutcomes }));
  }

  updateVisitOutcomeToEncounter(practiceId: string, patientId: string, followUp: EncounterFollowUpVo) {
    this.clinicalEncounterStore.update(patientId, entity => ({ EncounterFollowUp: followUp }));
  }

  updateDiagramsToEncounter(patientId: string, encounterDiagrams: EncounterDiagramVo[]) {
    this.clinicalEncounterStore.update(patientId, entity => ({ EncounterDiagrams: encounterDiagrams }));
  }

  upsertClinicalCommunicationToEncounter(tenantId: string, patientId: string, communication: CommunicationVo) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      Communications: arrayUpsert(entity.Communications || [], communication.CommunicationId, communication, 'CommunicationId'),
    }));
  }

  updateCommunication(encounterId: string, communicationId: string, body: UpdateEncounterCommunication) {
    // return this.clinicalClient.updateCommunication(practiceid, encounterId, communicationId, indexId, body);
    return this.providerService.provider$.pipe(
      take(1),
      // upload to storage, call cloud function to save practices/000222/communications/id, + meta data,
      switchMap(provider =>
        this.merakiService.updateHtmlToStorage(provider.PracticeNumber, 'EncounterCommunications', communicationId, body.Content)
      )
    );
  }

  saveEncounter(providerId: string, patientId: string) {
    return this.encounterInProgress$(providerId, patientId).pipe(
      take(1),
      map(encounter => this.fixEncounterObject(encounter)),
      withLatestFrom(this.providerService.provider$),
      switchMap(
        ([encounter, provider]) =>
          this.merakiEncounterService.checkIfEncounterStillActive(provider.PracticeTenantId, patientId, encounter.EncounterId).pipe(
            switchMap(isActive =>
              !isActive
                ? of(null)
                : this.merakiEncounterService.saveEncounter(providerId, encounter.EncounterId, encounter).pipe(
                    withLatestFrom(this.providerService.isForge$, this.providerService.isHbcGo$),
                    switchTap(
                      ([result, isForge, isHbcGo]) =>
                        // HBC Go has no MyMPS visit to update — skip the updatevisit call alongside the forge skip.
                        isForge || isHbcGo
                          ? of(result)
                          : this.patientEventClient
                              .updatePatientEvent(encounter.PatientEventDetails.PracticeId, {
                                PatientEventDetails: {
                                  ...encounter.PatientEventDetails,
                                  BranchXRef: encounter.BranchXRef || encounter.PatientEventDetails.BranchXRef,
                                  // todo
                                  // BranchName:
                                  MedicalInsurance: encounter.MedicalInsurance || encounter.PatientEventDetails.MedicalInsurance,
                                },
                              })
                              .pipe(catchError(s => of(null))) // ignore if florence won't be succesful (low priority call)
                    ),
                    map(([result]) => result)
                  )
            ),
            catchError(err => {
              this.appInsightsService.trackException(err, 'save-consultation', { encounterVo: JSON.stringify(encounter) });
              throw err;
            })
          ) //throwError("Consultation already completed."))) ..
      )
    );
  }

  fixEncounterObject(encounter: EncounterVo): EncounterVo {
    // temp fix to resolve serialization issue, can be removed after few releases
    if (encounter.EncounterFollowUp?.FollowUpReasons?.length > 0) {
      if (typeof encounter.EncounterFollowUp.FollowUpReasons[0] !== 'string') {
        const complex = encounter.EncounterFollowUp?.FollowUpReasons[0] as any;
        return {
          ...encounter,
          EncounterFollowUp: { ...encounter.EncounterFollowUp, FollowUpReasons: [complex?.id] },
        };
      }
    }

    const encounterUpdated = this.removeDosageInstructionFromEncounter(encounter);

    return encounterUpdated;
  }

  completeEncounter(providerId: string, patientId: string): Observable<RestApiResultOfCompleteEncounterResult> {
    return this.encounterInProgress$(providerId, patientId).pipe(
      take(1),
      map(encounter => ({ ...encounter, ClinicalMetrics: encounter.ClinicalMetrics?.filter(s => !!s.Value) })),
      map(encounter => this.fixEncounterObject(encounter)),
      map(encounter => this.adjustMedicineDescriptions(encounter)),
      withLatestFrom(this.patientsService.patientById$(patientId).pipe(take(1))),
      map(([encounter, patient]) => ({
        ...encounter,
        PatientEventDetails: {
          ...encounter.PatientEventDetails,
          // refresh patient details to handle situation when info was outdated at the beginning when encounter created
          Patient: patient.details || encounter.PatientEventDetails.Patient,
        },
      })),
      withLatestFrom(this.providerService.provider$, this.providerService.isForge$),
      switchMap(([encounter, provider, isForge]) => {
        const forgePipeline = from(this.requireManualProcessing(encounter)).pipe(
          switchMap(requireManualProcessing =>
            this.merakiEncounterService.saveEncounter(providerId, patientId, encounter).pipe(
              switchMap(s => this.merakiEncounterService.completeEncounter(provider.PracticeNumber, encounter, requireManualProcessing)),
              switchTap(s => this.patientsService.addPatientTimelineEventsForEncounter(providerId, patientId, encounter)),
              switchTap(s => this.merakiPatientService.completeVisit(provider.PracticeNumber, encounter.PatientEventDetails)),
              switchTap(
                s =>
                  (encounter.EncounterFollowUp?.FollowUpRequired &&
                    this.patientsService.scheduleVisitFollowup(providerId, patientId, {
                      FollowUpReasons: encounter.EncounterFollowUp.FollowUpReasons,
                      EncounterId: encounter.EncounterId,
                      PatientCommunication: {
                        DoctorName: encounter.Provider.TreatingDoctorName,
                        FollowUpDate: encounter.EncounterFollowUp.DueDateForFollowUp,
                        PatientCellphone: encounter.EncounterFollowUp.FollowUpPatientCellphone,
                        PatientDOB: encounter.PatientEventDetails.Patient.PatientDetails.DateOfBirth,
                        PatientName: encounter.PatientEventDetails.Patient.PatientDetails.FirstName,
                        PatientSurname: encounter.PatientEventDetails.Patient.PatientDetails.Surname,
                        PracticeId: encounter.PracticeId,
                        PatientId: patientId,
                        CommunicationText: encounter.EncounterFollowUp.FollowUpMessage,
                      },
                    })) ||
                  of(null)
              )
            )
          )
        );
        const florencePipeline = this.clinicalClient
          .completeEncounter(providerId, encounter.EncounterId, {
            EncounterVo: {
              ...encounter,
              ClinicalMetrics: [],
            },
          })
          .pipe(
            // add e-script timeline events if required (only for mymps providers as florence backend being legacy and wasn't updated with e-scripting changes)
            switchMap(result =>
              result.Sucess
                ? forkJoin([
                    this.patientsService.addEscriptPatientTimelineEventsForEncounter(providerId, patientId, encounter),
                    this.patientsService.addQuestionnairePatientTimelineEventsForEncounter(providerId, patientId, encounter),
                  ]).pipe(
                    map(([dischemResult, questionnaireResult]) => {
                      const allSuccessful = dischemResult?.Success && questionnaireResult?.Success;
                      return {
                        ...result,
                        TimelineEvents: {
                          Success: allSuccessful,
                          dischemResult,
                          questionnaireResult,
                        },
                      };
                    }),
                    catchError(err => {
                      console.error('Failed to add timeline events:', err);
                      return of({
                        ...result,
                        TimelineEvents: {
                          Success: false,
                          error: err,
                        },
                      });
                    })
                  )
                : of(result)
            )
          );
        return (isForge ? forgePipeline : florencePipeline).pipe(map(result => ({ result, encounter, provider })));
      }),
      switchTap(({ result, encounter, provider }) =>
        result.Sucess
          ? this.merakiEncounterService.updateEncounterStatus(provider.PracticeTenantId, encounter.EncounterId, 'Completed').pipe(
              catchError(err => {
                console.error('update status: ' + err);
                this.appInsightsService.trackException(err, 'updateEncounterStatus', { encounterId: encounter.EncounterId });
                // updating status is not critical for now, as this will do florence backend, so we can say success:true
                return of({ Sucess: true });
              })
            )
          : of(null)
      ),
      switchTap(({ result, encounter, provider }) => {
        if (encounter.ClinicalMetrics?.length > 0) {
          return this.capturePatientClinicalMetrics(provider.PracticeTenantId, patientId, encounter.ClinicalMetrics);
        }
        return of(null);
      }),
      catchError((err, req) => {
        console.error('complete consultation failed: ', err);
        const errorMessage = err?.ResponseMessage || err?.error || err?.message || '';
        this.appInsightsService.trackTrace('completeEncounterError', { patientId: patientId, message: errorMessage });
        return of({
          result: {
            Sucess: false,
            ...err,
            ResponseMessage: errorMessage,
          },
        });
      }),
      tap(({ result, encounter }) => {
        if (result.Sucess) {
          // Push ad impressions on a successful save
          const adImpressions = this.getAdImpressions(encounter);
          if (adImpressions.length > 0) {
            this.merakiService.saveAdImpressions(adImpressions);
          }
          this.clinicalEncounterStore.remove(patientId);
          this.encounterLettersStore.remove(patientId);
          this.providerService.removeVisitFromWaitingRoom(encounter.PatientEventDetails.PatientEventId);
        } else if (result.Code == ERROR_CODE.DUPLICATE_REQUEST) {
          // todo handle duplicate error?
        }
      }),
      map(result => result.result)
    );
  }

  adjustMedicineDescriptions(encounter: EncounterVo): EncounterVo {
    // update medicine line items to append q-ty
    return {
      ...encounter,
      EncounterLineItems: {
        ...encounter.EncounterLineItems,
        LineItems: encounter.EncounterLineItems.LineItems.map(line => {
          if (line.LineType === 'Medicine') {
            return {
              ...line,
              ChargeDesc: line.ChargeDesc + ' (' + line.ChargeQuan + ')',
            };
          }
          return line;
        }),
      },
      MedicationsPrescriptions: encounter.MedicationsPrescriptions.map(prescription => ({
        ...prescription,
        MedicationPrescriptionLines: prescription.MedicationPrescriptionLines.map(line => ({
          ...line,
          Description: line.Description + ' (' + line.Quantity + ')',
        })),
      })),
    };
  }

  getPatientInProgressEncounterId(patientId: string) {
    return this.clinicalEncounterQuery.getPatientEncounterId(patientId);
  }

  addModifierToProceduresOnEncounter(tenantId: string, patientId: string, modifierLineItem: EncounterLineItemVo, procedureCodes: string[]) {
    const encounter = this.clinicalEncounterQuery.getEntity(patientId);
    const encounterLines = encounter.EncounterLineItems.LineItems;

    // Populate modifier with existing diagnosis codes from the encounter
    const modifierWithDiagnosis = {
      ...modifierLineItem,
      Amount: modifierLineItem.Amount || 0,
      Diagnosis: encounter.Diagnosis || [],
    };

    const reorderedLines =
      procedureCodes.length === 0
        ? [...encounterLines, { ...modifierWithDiagnosis, LineNum: encounterLines.length + 1 }]
        : encounterLines.reduce((lines, encounterLine) => {
            if (encounterLine.LineType === 'Procedure' && procedureCodes.includes(encounterLine.ChargeCode)) {
              return [
                ...lines,
                { ...encounterLine, LineNum: lines.length + 1 },
                {
                  ...modifierWithDiagnosis,
                  LineNum: lines.length + 2,
                },
              ];
            } else {
              return [...lines, { ...encounterLine, LineNum: lines.length + 1 }];
            }
          }, [] as EncounterLineItemVo[]);

    this.clinicalEncounterStore.update(patientId, entity => ({
      EncounterLineItems: {
        ...entity.EncounterLineItems,
        LineItems: reorderedLines,
      },
    }));
  }

  upsetAssistingProviderName(tenantId: string, patientId: string, providerName: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      AssistingDoctorName: providerName,
    }));
  }

  upsetAssistingProviderPracticeNumber(tenantId: string, patientId: string, practiceNumber: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      AssistingDoctorPracticeNumber: practiceNumber,
    }));
  }

  saveHtmlToPdf(html: string, watermark?: string, password?: string) {
    return this.clinicalClient.saveHtmlToPdf(html, watermark, password);
  }

  saveHtmlToPdfUrl(html: string, practiceId: string, accessToken: string, saveAs: string = null) {
    return this.clinicalClient
      .saveHtmlToPdf(html, null)
      .pipe(map(data => this.getDownloadFileUrl(practiceId, accessToken, data.Data, saveAs)));
  }

  getDownloadFileUrl(practiceId: string, accessToken: string, filePath: string, saveAs: string = null) {
    const base = `${this.baseUrl}/api/v1/document`;
    const params = [
      `url=${encodeURIComponent(filePath)}`,
      `access_token=${encodeURIComponent(accessToken)}`,
      `practiceId=${encodeURIComponent(practiceId)}`,
    ];
    if (saveAs) {
      params.push(`saveAs=${encodeURIComponent(saveAs)}`);
    }
    return `${base}?${params.join('&')}`;
  }

  sendCommunication(providerId: string, patientId: string, email: ProviderCommunicationEmailVo) {
    return this.clinicalPatientClient.sendCommunication(providerId, patientId, email);
  }

  diagnosesById$(practiceId: string, patientId: string) {
    return this.encounterInProgress$(practiceId, patientId).pipe(map(encounter => encounter.Diagnosis || []));
  }

  diagnosisTokensById$(practiceId: string, patientId: string) {
    return this.encounterInProgress$(practiceId, patientId).pipe(map(encounter => encounter.DiagnosisTokenItems || []));
  }

  encounterLinesById$(practiceId: string, patientId: string) {
    return this.encounterInProgress$(practiceId, patientId).pipe(map(encounter => encounter.EncounterLineItems.LineItems));
  }

  encounterHeaderById$(practiceId: string, patientId: string) {
    return this.encounterInProgress$(practiceId, patientId).pipe(map(encounter => encounter.EncounterLineItems.EncounterHeader));
  }

  encounterPrescriptionsById$(practiceId: string, patientId: string) {
    return this.encounterInProgress$(practiceId, patientId).pipe(map(encounter => encounter.MedicationsPrescriptions));
  }

  patientMetricsById(practiceId: string, patientId: string) {
    return combineLatest([this.clinicalEncounterQuery.selectEntity(patientId), this.referenceDataService.clinicalMetrics$]).pipe(
      map(([entity, clinicalMetric]) => {
        if (!entity || !entity.ClinicalMetrics) {
          return [];
        }
        const metrics = entity.ClinicalMetrics;
        const groupedMetrics = _.chain(metrics)
          .map(metric => {
            const metricData = clinicalMetric['ClinicalMetric:' + metric.Name];
            return {
              value: metric.Value,
              valueUnit: metric.Unit,
              valueFlag: metric.Type,
              date: new Date(metric.TestDate),
              ...metricData,
            } as IClinicalMetricValue;
          })
          .groupBy('type')
          .sortBy('type')
          .map(group => {
            const orderedResults = _.chain(group).sortBy('Date').reverse();
            const firstResult = orderedResults.first().value();
            return {
              allResults: orderedResults.value(),
              ...firstResult,
            };
          })
          .value();
        return groupedMetrics;
      })
    );
  }

  upsetClinicalMetricsToEncounter(tenantId: string, patientId: string, metrics: ClinicalMetricVo[]) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      ClinicalMetrics: metrics,
    }));
  }

  upsetClinicalMetricToEncounter(tenantId: string, patientId: string, metric: ClinicalMetricVo) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const findFn = item => item.Group === metric.Group && item.Name === metric.Name;
      return {
        ClinicalMetrics:
          entity.ClinicalMetrics.filter(findFn).length > 0
            ? arrayUpdate(entity.ClinicalMetrics, item => item.Group === metric.Group && item.Name === metric.Name, metric)
            : arrayAdd(entity.ClinicalMetrics, metric),
      };
    });
  }

  removeClinicalMetricFromEncounter(tenantId: string, patientId: string, metricId: string, metricGroup: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      ClinicalMetrics: arrayRemove(entity.ClinicalMetrics, item => item.Group === metricGroup && item.Name === metricId),
    }));
  }

  removeClinicalCommunicationFromEncounter(tenantId: string, patientId: string, communicationId: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      Communications: arrayRemove(entity.Communications, communicationId, 'CommunicationId'),
    }));
  }

  hasInProgressPatientEncounter(practiceId: string, patientId: string) {
    return this.clinicalEncounterQuery.patientHasInProgressEncounter$(patientId);
  }

  searchProcedures(practiceId: string, lookupfilter: string, dateOfService: Date) {
    // todo switch to meily index
    // Wait for the reference data instead of sampling it (same first-search
    // drop as searchModifiers)
    return combineLatest([
      this.providerService.isForge$,
      this.referenceDataService.tarrifMedicalTypeCodes$,
      this.providerService.provider$,
    ]).pipe(
      take(1),
      switchMap(([isForge, types, provider]) =>
        true // NB using meily index for all providers (reducing florence pressure)
          ? this.searchTariffCodeIndex(lookupfilter, this.getTarrifMedicalType(types, provider.SpecialityCode))
          : this.loadProcedureFromFlorence(practiceId, lookupfilter, dateOfService)
      )
    );
  }

  loadProcedureFromFlorence(practiceId: string, lookupfilter: string, dateOfService: Date): Observable<SearchResult[]> {
    return this.clinicalClient
      .getProcedures(practiceId, '00000000-0000-0000-0000-000000000001', lookupfilter, `${moment(dateOfService).format('YYYY-MM-DD')}`)
      .pipe(
        map(encs =>
          _.flattenDeep(
            encs.map(enc =>
              enc.LineItems.map(
                line =>
                  ({
                    Code: line.ChargeCode,
                    Description: line.ChargeDesc,
                  }) as SearchResult
              )
            )
          )
        )
      );
  }

  getTarrifMedicalType(types: { SpecialityCode: number; Description: string; Type: string }[], specialityCode: string): string {
    const medicalType = types.find(s => s.SpecialityCode == Number(specialityCode));
    return medicalType?.Type || 'MP';
  }

  searchTariffCodeIndex(lookupfilter: string, type: string): Observable<Hits<SearchResult>> {
    const index = this.meiliSearchLookupClient.index(`tariff_code`);
    const filter = ['TypeCode=10', `TariffMedicalTypeCode=${type}`];

    const res = index.search<SearchResult>(lookupfilter, {
      filter,
      limit: 10,
    });

    return from(res).pipe(
      // todo once confirmed uncomment #36722
      map(s => s.hits?.filter(hit => !hit.Code.endsWith('A')))
    );
  }

  getTariffCode(type: string, code: string): Promise<Hits<SearchResult>> {
    const index = this.meiliSearchLookupClient.index(`tariff_code`);
    const filter = ['TypeCode=10', `TariffMedicalTypeCode=${type}`, `Code=${code}`];

    const res = index.search<SearchResult>('', {
      filter,
      limit: 10,
    });

    return res.then(s => s.hits);
  }

  searchModifierCodeIndex(lookupfilter: string, type: string): Observable<Hits<SearchResult>> {
    const index = this.meiliSearchLookupClient.index(`tariff_code`);
    const filter = ['TypeCode=99', `TariffMedicalTypeCode=${type}`];

    const res = index.search<SearchResult>(lookupfilter, {
      filter,
      limit: 10,
    });

    return from(res).pipe(map(s => s.hits));
  }

  searchModifiers(practiceId: string, encounterId: string, lookupfilter: string) {
    // Wait for the reference data instead of sampling it: take(1) plus
    // withLatestFrom completed empty when the type codes were still loading,
    // silently swallowing the first search after a page load
    return combineLatest([this.providerService.provider$, this.referenceDataService.tarrifMedicalTypeCodes$]).pipe(
      take(1),
      switchMap(([provider, types]) =>
        this.searchModifierCodeIndex(lookupfilter, this.getTarrifMedicalType(types, provider.SpecialityCode))
      )
    );
  }

  searchPrescription(practiceId: string, encounterId: string, lookupfilter: string) {
    return this.clinicalClient.getPrescriptions(practiceId, encounterId, lookupfilter);
  }

  getModifierInformation(practiceId: string, code: string) {
    return this.clinicalClient.getModifiersInfo(practiceId, code);
  }

  async requireManualProcessing(encounter: EncounterVo): Promise<string[]> {
    var manualProcessingReasons: string[] = [];
    //1. Is Cash Account
    if (encounter.EncounterType == EncounterType.CashInvoice) {
      manualProcessingReasons.push('Cash account');
    }
    const account = encounter.PatientEventDetails.Patient.PatientAccountDetails;
    if (encounter.EncounterType == EncounterType.MedicalAidInvoice) {
      //6. Medical aid details are incomplete
      if (!account.MedicalAidName || !account.MedicalAidPlan || !account.MedicalAidPlanOption || !account.MedicalAidMembershipNumber) {
        manualProcessingReasons.push('Medical aid details are incomplete');
      }
      //7. Dependant Code not valid
      if (!account.MedicalAidDependentCode || !RegExp(AccountNoRegex).test(account.MedicalAidDependentCode)) {
        manualProcessingReasons.push('Dependant code is not valid');
      }
    }
    //3. Is HB to contact me selected
    var hbToContactMeSelected = encounter.RequiresFollowUp || encounter.Note?.length > 0;
    if (hbToContactMeSelected) {
      manualProcessingReasons.push("'admin to contact me' selected and / or comments provided");
    }

    //4. if Discovery KeyCare medical aid chosen with complex pricing logic
    /* var schemeName = (account.MedicalAidName ?? "").ToLower().Trim();
    if (provider?.ManualPricingSchemes != null &&
        provider.ManualPricingSchemes.Any(
            s => schemeName.Equals((s ?? "").ToLower().Trim())
                 || (s ?? "").Equals("*")))
    {
        manualProcessingReasons.push("Manual pricing scheme patient");
    } */
    //5. if account no. not provided
    var accountNo = account.AccountNo;
    if (!accountNo || !RegExp(AccountNoRegex).test(accountNo)) {
      manualProcessingReasons.push('Account number is missing or invalid');
    }
    //8. Cellphone not valid

    /*   var patientCellphone = encounter.PatientEventDetails.Patient.PatientDetails.ContactNo;
      var patientCellphoneValid = patientCellphone && RegExp(/^[0-9]*$/).test(patientCellphone);
      if (!patientCellphoneValid) {
        manualProcessingReasons.push('Patient cell phone is not valid');
      }

      var memberCellphone = account.MedicalAidMainMemberDetails.ContactNo;
      var memberCellphoneValid = memberCellphone && RegExp(/^[0-9]*$/).test(memberCellphone);
      if (!memberCellphoneValid) {
        manualProcessingReasons.push('Medical aid main member cell phone is not valid');
      } */
    //9. check if doctor decided to get invoices over email
    /* var divertInvoiceToEmail = GetDivertInvoiceToEmail(provider);
    if (!string.IsNullOrEmpty(divertInvoiceToEmail) &&
      EmailRegex.IsMatch(divertInvoiceToEmail)) {
      manualProcessingReasons.Add("Doctor configured to receive invoice over email");
    } */
    //10. validate dateOfBirth
    var minDate = moment('1900-01-01').toDate();
    var dobValid =
      moment(account.MedicalAidMainMemberDetails.DateOfBirth).toDate() >= minDate &&
      moment(encounter.PatientEventDetails.Patient.PatientDetails.DateOfBirth).toDate() >= minDate;

    if (!dobValid) {
      manualProcessingReasons.push('Patient/main member date of birth is not valid');
    }

    const modifiers = encounter.EncounterLineItems.LineItems.filter(line => line.LineType === 'Modifier');
    if (modifiers.length > 0) {
      // Check for admin modifiers that require manual processing
      const adminModifiers = modifiers.filter(m => ADMIN_MODIFIER_CODES.includes(m.ChargeCode));
      if (adminModifiers.length > 0) {
        adminModifiers.forEach(m => {
          manualProcessingReasons.push(`Unsupported modifier: ${m.ChargeCode}`);
        });
      }

      const modifierInfos = await Promise.all(
        modifiers.map(m => this.getModifierInformation(encounter.PatientEventDetails.PracticeId, m.ChargeCode).toPromise())
      );
      // Exclude admin modifiers from the not calculated check since they have their own handling
      const notCalculatedModifiers = modifierInfos.filter(
        m => m.ImplementationStatus !== 'Calculated' && !ADMIN_MODIFIER_CODES.includes(m.Code)
      );
      if (notCalculatedModifiers.length > 0) {
        manualProcessingReasons.push('Contains not calculated modifier(s): ' + notCalculatedModifiers.map(m => m.Code).join(','));
      }
    }

    return manualProcessingReasons;
  }

  addSymptoms(practiceId: string, patientId: string, symptom: Symptom[]) {
    symptom.map(symp => this.addSymptom(practiceId, patientId, symp));
  }

  addSymptom(practiceId: string, patientId: string, symptom: Symptom, practiceNumber: string = '') {
    const currentSymptoms = this.clinicalEncounterQuery.getEntity(patientId).Symptoms;
    // don't add if we already added the symptom
    if (!currentSymptoms || !currentSymptoms.some(s => s.SymptomId === symptom.SymptomId)) {
      this.clinicalEncounterStore.update(patientId, entity => ({ Symptoms: arrayAdd(entity.Symptoms, symptom) }));

      if (symptom.Type === 'custom') {
        this.merakiLookupService
          .getVisitReasonQuestionsByPractice(practiceNumber, symptom.SymptomId)
          .pipe(take(1))
          .subscribe({
            next: symptomQuestions => this.addSymptomToEncounter(practiceId, patientId, symptom, symptomQuestions),
          });
      } else {
        // add the questions related to the symptom to the UI state
        this.merakiLookupService
          .getVisitReasonQuestions(symptom.SymptomId)
          .pipe(take(1))
          .subscribe({
            next: symptomQuestions => this.addSymptomToEncounter(practiceId, patientId, symptom, symptomQuestions),
          });
      }
    }
  }

  addGenericSymptom(practiceId: string, patientId: string, title: string) {
    const GENERIC = 'generic';

    const symptom = {
      SymptomId: `${GENERIC}-${title}`,
      Name: `${title.replace('-', ' ')}`,
      Type: 'complaint',
      SpecialityCodes: [GENERIC],
    } as Symptom;

    const currentSymptoms = this.clinicalEncounterQuery.getEntity(patientId).Symptoms;

    if (!currentSymptoms || !currentSymptoms.some(s => s.SymptomId === title)) {
      this.clinicalEncounterStore.update(patientId, entity => ({ Symptoms: arrayAdd(entity.Symptoms, symptom) }));

      // combineLatest([
      //   this.referenceDataService.getSymptomQuestions(GENERIC).pipe(map(g => g.map(s => ({ ...s, SymptomId: symptom.SymptomId })))),
      //   this.referenceDataService.getQuestionsBySymptom(GENERIC),
      //   this.referenceDataService.getQuestions(),
      // ])

      this.merakiLookupService
        .getVisitReasonQuestions(GENERIC)
        .pipe(
          take(1),
          map(g => g.map(s => ({ ...s, SymptomId: symptom.SymptomId })))
        )
        .subscribe({
          next: symptomQuestions => this.addSymptomToEncounter(practiceId, patientId, symptom, symptomQuestions),
        });
    }
  }

  private addSymptomToEncounter(practiceId: string, patientId: string, symptom: Symptom, symptomQuestions: SymptomQuestion[]) {
    const questionsForSymptom = symptomQuestions
      .filter(s => s.SymptomId === symptom.SymptomId)
      .map(s => {
        const question = s.Question;

        // apply some logic to change some values around
        const isShared = question ? question.SharedValue : false;
        const QuestionKey = !isShared ? `${s.SymptomId}-${question.QuestionKey}` : question.QuestionKey;

        const existingQuestions = this.clinicalEncounterQuery.getEntity(patientId).SymptomQuestions;

        if (!existingQuestions.some(q => q.QuestionKey === QuestionKey)) {
          return this.ConvertToClinicalEncounterSymptomQuestion(symptom, s);
        } else {
          return null;
        }
      });

    this.addQuestionsForSymptom(
      practiceId,
      patientId,
      questionsForSymptom.filter(q => !!q)
    );
  }

  private ConvertToClinicalEncounterSymptomQuestion(symptom: Symptom, symptQuestion: SymptomQuestion) {
    // find the referenced question
    // const question =  questions.map(q => ({ ...q, Order: symptQuestion.Order })).find(q => q.QuestionKey === symptQuestion.QuestionKey);
    const question = { ...symptQuestion.Question, Order: symptQuestion.Order || null };

    // apply some logic to change some values around
    const isShared = question ? question.SharedValue : false;
    const QuestionKey = !isShared ? `${symptQuestion.SymptomId}-${question.QuestionKey}` : question.QuestionKey;

    return {
      SymptomId: symptom.SymptomId,
      QuestionKey, // override the key if necessary
      Section: symptQuestion.Section,

      Question: {
        ...question,
        QuestionKey, // override the key if necessary
      },

      Dependency: symptQuestion.Dependency
        ? {
            ...symptQuestion.Dependency,
            DependencyKeyName: `${symptQuestion.SymptomId}-${symptQuestion.Dependency.DependencyKeyName}`,
          }
        : null,

      Questions: symptQuestion.Questions
        ? symptQuestion.Questions.map(q => this.ConvertToClinicalEncounterSymptomQuestion(symptom, q))
        : null,
    } as SymptomQuestion;
  }

  updateSymptom(practiceId: string, patientId: string, symptom: Symptom) {
    const currentSymptoms = this.clinicalEncounterQuery.getEntity(patientId).Symptoms;
    if (!currentSymptoms.some(s => s.SymptomId === symptom.SymptomId)) {
      // only update it if it exists
      this.clinicalEncounterStore.update(patientId, entity => ({
        Symptoms: arrayUpdate(entity.Symptoms, s => s.SymptomId === symptom.SymptomId, symptom),
      }));
    }
  }

  removeSymptom(practiceId: string, patientId: string, symptomId: string) {
    const currentSymptoms = this.clinicalEncounterQuery.getEntity(patientId).Symptoms;
    if (currentSymptoms.some(s => s.SymptomId === symptomId)) {
      // only remove it if you find it
      this.clinicalEncounterStore.update(patientId, entity => ({
        Symptoms: arrayRemove(entity.Symptoms, s => s.SymptomId === symptomId),
        SymptomQuestions: arrayRemove(entity.SymptomQuestions, s => s.SymptomId === symptomId),
        SymptomQuestionAnswers: arrayRemove(entity.SymptomQuestionAnswers, s => s.SymptomId === symptomId),
      }));
    }
  }

  addQuestionForSymptom(practiceId: string, patientId: string, question: SymptomQuestion) {
    this.clinicalEncounterStore.update(patientId, entity => ({ SymptomQuestions: arrayAdd(entity.SymptomQuestions, question) }));
  }

  addQuestionsForSymptom(practiceId: string, patientId: string, question: SymptomQuestion[]) {
    this.clinicalEncounterStore.update(patientId, entity => ({ SymptomQuestions: arrayAdd(entity.SymptomQuestions, question) }));
  }

  removeQuestionForSymptom(practiceId: string, patientId: string, QuestionKey: string) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      SymptomQuestions: arrayRemove(entity.SymptomQuestions, q => q.QuestionKey === QuestionKey),
      SymptomQuestionAnswers: arrayRemove(entity.SymptomQuestionAnswers, q => q.QuestionKey === QuestionKey),
    }));
  }

  updateSymptomQuestions(practiceId: string, patientId: string, SymptomQuestions: SymptomQuestion[]) {
    this.clinicalEncounterStore.update(patientId, { SymptomQuestions });
  }

  updateSymptomQuestion(practiceId: string, patientId: string, symptomQuestion: SymptomQuestion) {
    this.clinicalEncounterStore.update(patientId, entity => ({
      SymptomQuestions: arrayUpdate(
        entity.SymptomQuestions,
        s => s.SymptomId === symptomQuestion.SymptomId && s.QuestionKey === symptomQuestion.QuestionKey,
        symptomQuestion
      ),
    }));
  }

  updateSymptomAnswer(
    practiceId: string,
    patientId: string,
    SymptomId: string,
    QuestionKey: string,
    AnswerValue: any,
    NoteValue: any,
    Section: SymptomQuestionSection,
    Notes: string
  ) {
    this.clinicalEncounterQuery
      .selectEntity(patientId, ent => ent.SymptomQuestionAnswers)
      .pipe(
        take(1),
        tap(stateAnswers => {
          let answer: SymptomQuestionAnswer = _.cloneDeep(
            stateAnswers.find(a => a.QuestionKey === QuestionKey && a.SymptomId === SymptomId && a.Section === Section)
          );

          if (answer) {
            // check existing answer
            if (answer.AnswerValue !== AnswerValue || answer.Notes !== Notes) {
              // state is different, update
              answer.AnswerValue = AnswerValue;
              answer.NoteValue = NoteValue;
              answer.Notes = Notes;

              this.clinicalEncounterStore.update(patientId, entity => ({
                SymptomQuestionAnswers: arrayUpdate(
                  entity.SymptomQuestionAnswers,
                  a => a.QuestionKey === QuestionKey && a.SymptomId === SymptomId,
                  answer
                ),
              }));
            }
          } else {
            // answer doesn't exist, add it
            answer = {
              SymptomId,
              QuestionKey,
              AnswerValue,
              NoteValue,
              Section,
              Notes,
            };
            this.clinicalEncounterStore.update(patientId, entity => ({
              SymptomQuestionAnswers: arrayAdd(entity.SymptomQuestionAnswers, answer),
            }));
          }
        })
      )
      .subscribe();
  }

  getPatientSymptoms(practiceId: string, patientId: string) {
    return this.clinicalEncounterQuery.selectEntity(patientId, enc => enc.Symptoms);
  }

  getPatientSymptomQuestions(practiceId: string, patientId: string, section: SymptomQuestionSection) {
    return this.clinicalEncounterQuery
      .selectEntity(patientId, enc => enc.SymptomQuestions)
      .pipe(map(questions => questions.filter(q => !section || q.Section === section)));
  }

  getPatientSymptomQuestionsAndAnswers(practiceId: string, patientId: string, section: SymptomQuestionSection) {
    return this.clinicalEncounterQuery
      .selectEntity(patientId, enc => ({ questions: enc.SymptomQuestions, answer: enc.SymptomQuestionAnswers }))
      .pipe(
        map(com => ({
          questions: com.questions.filter(q => !section || q.Section === section),
          answers: com.answer.filter(q => !section || q.Section === section),
        }))
      );
  }

  getPatientSymptomAndQuestions(practiceId: string, patientId: string, section: SymptomQuestionSection) {
    return combineLatest([
      this.clinicalEncounterQuery.selectEntity(patientId, enc => enc.Symptoms).pipe(distinctUntilChanged()),
      this.clinicalEncounterQuery.selectEntity(patientId, enc => enc.SymptomQuestions).pipe(distinctUntilChanged()),
    ]).pipe(
      map(([stateSymptoms, stateSymptomQuestions]) =>
        !stateSymptoms
          ? []
          : stateSymptoms.map(symptom => ({
              ...symptom,
              Questions: (stateSymptomQuestions || [])
                .filter(q => q.SymptomId === symptom.SymptomId)
                .filter(q => !section || q.Section === section)
                .map(q => ({
                  ...q.Question,
                  Dependency: q.Dependency,
                  Questions: q.Questions,
                })),
            }))
      )
    );
  }

  getPatientSymptomQuestionAnswers(practiceId: string, patientId: string, section?: SymptomQuestionSection) {
    return combineLatest([
      this.clinicalEncounterQuery.selectEntity(patientId, enc => enc.SymptomQuestionAnswers),
      this.clinicalEncounterQuery.selectEntity(patientId, enc => enc.SymptomQuestions),
    ]).pipe(
      map(([answers, questions]) =>
        !answers
          ? null
          : answers
              .filter(a => !section || a.Section === section)
              .map(
                a =>
                  ({
                    ...a,
                    Question: questions.find(q => q.QuestionKey === a.QuestionKey),
                  }) as SymptomQuestionAnswer
              )
      )
    );
  }

  setEncounterDefaults(practiceId, patientId) {
    return this.providerService.branches$.pipe(
      withLatestFrom(this.providerService.provider$, this.providerService.isMymps$),
      map(([branches, provider, isMymps]) =>
        branches.map(m => ({
          BranchName: m.BranchName,
          BranchXRef: m.BranchXRef,
          Enabled: m.Enabled,
          IsMainBranch: m.IsMainBranch,
          IsDefault:
            m.ProviderInfos?.some(s =>
              isMymps
                ? provider.PracticeXRef == s.ProviderXRef && s?.IsDefaultBranch
                : provider.HPCSANumber == s.ProviderXRef && s?.IsDefaultBranch
            ) ?? false,
        }))
      ),
      tap(branches => {
        this.clinicalEncounterStore.update(patientId, entity => {
          return {
            ...entity,
            BranchXRef: entity?.BranchXRef ?? branches.find(f => f.IsDefault)?.BranchXRef ?? '',
          };
        });
      })
    );
  }

  priceEncounter(practiceId: string, patientId: string, onlyMissingPrices: boolean = false) {
    const encounter = this.clinicalEncounterQuery.getEntity(patientId);

    if (!encounter) {
      return of({
        success: false,
        encounter: null,
        error: 'could not find encounter',
        tenantId: practiceId,
        patientId,
      });
    }

    const encounterUpdated = this.removeDosageInstructionFromEncounter(encounter);

    return this.priceEncounterForProvider(practiceId, encounterUpdated, onlyMissingPrices).pipe(
      map(result => {
        if (result && result.Sucess) {
          const serverEncounter = result.Data;

          const stateLines = this.clinicalEncounterQuery.getEntity(patientId)?.EncounterLineItems?.LineItems;
          serverEncounter?.EncounterLineItems?.LineItems.forEach(serverLine => {
            const stateLine = stateLines?.find(l => l.LineNum === serverLine.LineNum);

            if (!!stateLine && !!serverLine) {
              const newStateLine: EncounterLineItemVo = {
                ...stateLine,
                Amount: stateLine.PriceOverride ? stateLine.Amount : serverLine.Amount,
                TotalExcVat: serverLine.TotalExcVat,
                TotalIncVat: serverLine.TotalIncVat,
                UnitPrice: serverLine.UnitPrice,
              };

              this.clinicalEncounterStore.update(patientId, entity => ({
                ...entity,
                EncounterLineItems: {
                  ...entity.EncounterLineItems,
                  LineItems: arrayUpdate(entity.EncounterLineItems.LineItems, l => l.LineNum === newStateLine.LineNum, newStateLine),
                },
              }));
            }
          });

          return { success: true, encounter: serverEncounter, error: null, tenantId: practiceId, patientId };
        } else {
          return { success: false, encounter: null, error: result.ResponseMessage, tenantId: practiceId, patientId };
        }
      }),
      catchError(error => {
        // Handle errors thrown by priceEncounterForProvider (e.g., admin modifier validation)
        return of({
          success: false,
          encounter: null,
          error: typeof error === 'string' ? error : error.message || 'Unable to price encounter',
          tenantId: practiceId,
          patientId,
        });
      })
    );
  }

  private priceEncounterForProvider(
    practiceId: string,
    encounter: EncounterVo,
    onlyMissingPrices: boolean = false
  ): Observable<RestApiResultOfEncounterVo> {
    // Check if admin modifiers are present without "Send to admin" flag
    const hasAdminModifiers = encounter.EncounterLineItems.LineItems.some(
      line => line.LineType === 'Modifier' && ADMIN_MODIFIER_CODES.includes(line.ChargeCode)
    );

    if (hasAdminModifiers && !encounter.RequiresFollowUp) {
      const adminModifierCodes = encounter.EncounterLineItems.LineItems.filter(
        line => line.LineType === 'Modifier' && ADMIN_MODIFIER_CODES.includes(line.ChargeCode)
      )
        .map(line => line.ChargeCode)
        .join(', ');
      const modifierLabel = adminModifierCodes.includes(',') ? 'Modifiers' : 'Modifier';
      return throwError(`${modifierLabel} ${adminModifierCodes} cannot be priced. Please enable 'Send to admin' to process this invoice.`);
    }

    return this.providerService.provider$.pipe(
      take(1),
      withLatestFrom(this.providerService.isForge$.pipe(take(1)), this.providerService.isMultiBranch$.pipe(take(1))),
      switchMap(([provider, isForge, isMultiBranch]) =>
        isForge
          ? this.merakiEncounterService
              .priceEncounter(practiceId, encounter.EncounterId, encounter, provider, onlyMissingPrices, isMultiBranch)
              .pipe(
                switchMap(result => {
                  // use refresh endpoint to apply modifier calculations logic if any modifier added
                  if (result && result.Sucess && encounter.EncounterLineItems.LineItems.find(item => item.LineType == 'Modifier') != null) {
                    return this.clinicalClient.refreshEncounter(practiceId, encounter.EncounterId, { Encounter: result.Data });
                  }
                  return of(result);
                })
              )
          : this.clinicalClient
              .priceEncounter(
                practiceId,
                encounter.EncounterId,
                // Filter out admin modifiers only if RequiresFollowUp (Send to admin) is set
                encounter.RequiresFollowUp
                  ? {
                      ...encounter,
                      EncounterLineItems: {
                        ...encounter.EncounterLineItems,
                        LineItems: encounter.EncounterLineItems.LineItems.filter(
                          line => !(line.LineType === 'Modifier' && ADMIN_MODIFIER_CODES.includes(line.ChargeCode))
                        ),
                      },
                    }
                  : encounter
              )
              .pipe(
                map(result => {
                  // Restore admin modifiers to the result after pricing (only if they were excluded)
                  if (result && result.Sucess && encounter.RequiresFollowUp) {
                    const adminModifierLines = encounter.EncounterLineItems.LineItems.filter(
                      line => line.LineType === 'Modifier' && ADMIN_MODIFIER_CODES.includes(line.ChargeCode)
                    );
                    if (adminModifierLines.length > 0) {
                      return {
                        ...result,
                        Data: {
                          ...result.Data,
                          EncounterLineItems: {
                            ...result.Data.EncounterLineItems,
                            LineItems: [...result.Data.EncounterLineItems.LineItems, ...adminModifierLines],
                          },
                        },
                      };
                    }
                  }
                  return result;
                })
              )
      )
    );
  }

  removeDosageInstruction(parameters: { [key: string]: string }): { [key: string]: string } {
    const { DosageInstructions, ...rest } = parameters;
    return rest;
  }

  removeDosageInstructionFromEncounter(encounter) {
    return {
      ...encounter,
      EncounterLineItems: {
        ...encounter.EncounterLineItems,
        LineItems: encounter.EncounterLineItems.LineItems.map(m => ({
          ...m,
          Parameters: m.Parameters ? this.removeDosageInstruction(m.Parameters) : m.Parameters,
        })),
      },
      MedicationsPrescriptions: encounter.MedicationsPrescriptions.map(m => ({
        ...m,
        MedicationPrescriptionLines: m.MedicationPrescriptionLines.map(p => ({
          ...p,
          Description: `${p.MedicationDescription} ${p.Parameters.DosageDescription}`,
          Parameters: p.Parameters ? this.removeDosageInstruction(p.Parameters) : p.Parameters,
        })),
      })),
    };
  }

  updateEncounter(tenantId: string, patientId: string, encounter: EncounterVo) {
    this.clinicalEncounterStore.update(patientId, this.reorderEncounterLinesForEncounter(encounter));
  }

  updateEncounterFromProviderConfigs(tenantId: string, patientId: string, configs: PatientConfigurationVo) {
    this.clinicalEncounterStore.update(patientId, entity => {
      const sendClaimToAdminByScheme = configs?.Configuration[ProviderConfigurationKey.SendClaimToAdminByScheme]?.split(',') || null;
      const patientMedicalAid = entity?.PatientEventDetails?.Patient?.PatientAccountDetails?.MedicalAidName || null;

      if (!sendClaimToAdminByScheme || !patientMedicalAid) {
        return entity;
      }

      return {
        ...entity,
        RequiresFollowUp: sendClaimToAdminByScheme.includes(patientMedicalAid.toLowerCase()),
      };
    });
  }

  updateEncounterPlaceOfService(tenantId: string, patientId: string, placeOfService: EncounterHeaderVoPlaceOfService) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      EncounterLineItems: {
        ...encounter.EncounterLineItems,
        EncounterHeader: {
          ...encounter.EncounterLineItems.EncounterHeader,
          PlaceOfService: placeOfService,
        },
      },
    }));
  }

  updateEncounterLine(tenantId: string, patientId: string, line: EncounterLineItemVo) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      EncounterLineItems: {
        ...encounter.EncounterLineItems,
        LineItems: arrayUpdate(encounter.EncounterLineItems.LineItems, entity => entity.LineNum === line.LineNum, line),
      },
    }));
  }

  updateSpeechToNotesNoteVersion(tenantId: string, encounterId: string, noteVersion: number, recordingDuration: number, micDevice: string) {
    return this.merakiService.updateSpeechToNotesNoteVersion(tenantId, encounterId, noteVersion, recordingDuration, micDevice);
  }

  updateSpeechToNotes(tenantId: string, encounterId: string, updatedNotes: string, treatingDoctorName: string, dateOfService: Date) {
    return this.merakiService.updateSpeechToNotes(tenantId, encounterId, updatedNotes, treatingDoctorName, dateOfService);
  }

  updateClinicalNote(practiceId: string, patientId: string, clinicalNote: string) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      ClinicalNote: clinicalNote,
    }));
  }

  clinicalNoteById$(practiceId: string, patientId: string) {
    return this.clinicalEncounterQuery.selectEntity(patientId, encounter => encounter.ClinicalNote);
  }

  appendClinicalNote(practiceId: string, patientId: string, clinicalNote: string) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      ClinicalNote: (encounter.ClinicalNote || '') + clinicalNote,
    }));
  }

  updateAdminNote(practiceId: string, patientId: string, note: string) {
    this.clinicalEncounterStore.update(patientId, encounter => ({
      Note: note,
    }));
  }

  // `documentTenantId` scopes the patientdocument upload (legacy mps-api,
  // keyed by PracticeId), while `tenantId` scopes the encounter save (Meraki,
  // keyed by PracticeTenantId). In main HBC these are the same value so the
  // default keeps existing callers unchanged; HBC-Go must pass them separately.
  @action('uploadEncounterFile')
  uploadEncounterFile(tenantId: string, patientId: string, documentVo: PatientDocumentVo, file: Blob, documentTenantId: string = tenantId) {
    return this.patientsService.uploadPatientFile(documentTenantId, patientId, documentVo, file).pipe(
      map(response => response.document),
      tap(document => {
        this.clinicalEncounterStore.update(patientId, encounter => ({
          PatientDocuments: arrayAdd(encounter.PatientDocuments || [], {
            Category: document.Category,
            DocumentUrl: document.DocumentUrl,
          }),
        }));
      }),
      switchMap(() => this.saveEncounter(tenantId, patientId))
    );
  }

  uploadEncounterDiagram(tenantId: string, patientId: string, documentVo: PatientDocumentVo, file: Blob) {
    return this.patientsService.uploadPatientFile(tenantId, patientId, documentVo, file).pipe(map(response => response.document));
  }

  toggleQuickNotes() {
    this.showQuickNotesOnEncounterSummary$.next(!this.showQuickNotesOnEncounterSummary$.value);
  }

  predictionIcd(tenantId: string, request: ICDPredictionRequest) {
    return this.clinicalNewClient.predictionIcd(tenantId, request);
  }

  updateEncounterUiState(tenantId: string, patientId: string, uiState: Partial<ClinicalEncounterUIViewModel>) {
    this.clinicalEncounterStore.ui.update(patientId, uiState);
  }

  incrementDisplayCategories(tenantId: string, patientId: string) {
    this.clinicalEncounterStore.ui.update(patientId, state => {
      const newState = {
        ...state,
        categoriesToShow: (state?.categoriesToShow || DEFAULT_CATEGORIES) + 1,
        subCategoriesToDisplay: (state?.subCategoriesToDisplay || DEFAULT_CATEGORIES) + 1,
      };
      return newState;
    });
  }

  searchDiagnosis(tenantId: string, encounterId: string, icd: string) {
    // compatibility fix to allow get diagnosis from florence backend when algolia gave icd code with / instead of . (M8633/1, etc)
    return this.clinicalClient.getDiagnosisRanking(tenantId, encounterId, icd.replace('/', '.')).pipe(
      map(s =>
        s?.map(d => ({
          ...d,
          EncounterHeader: {
            ...d.EncounterHeader,
            Diagnosis: d.EncounterHeader.Diagnosis.map(diag => ({ ...diag, DiagnosisCode: icd })),
          },
        }))
      )
    );
  }

  getDagnosisToken(diagnosis: DiagnosisVo2): Observable<DiagnosisVo2> {
    return this.algoliaRestService
      .getAlgoliaDiagnosisToken(diagnosis.DiagnosisCode)
      .pipe(map(DiagnosisToken => ({ ...diagnosis, DiagnosisToken }) as DiagnosisVo2));
  }

  upsertDiagnosisCodeToEncounter(tenantId: string, patientId: string, icdCode: string) {
    return this.encounterInProgress$(tenantId, patientId).pipe(
      switchMap(encounter => this.searchDiagnosis(tenantId, encounter.EncounterId, icdCode)),
      map(results => {
        if (!results) {
          return null;
        }
        const diagnosis =
          results &&
          results.flatMap(line => line.EncounterHeader.Diagnosis.flatMap(diag => diag)).find(diag => diag.DiagnosisCode === icdCode);
        return diagnosis;
      }),
      tap(diagnosis => {
        if (!diagnosis) {
          console.error('Could not find diagnosis');
        }
      }),
      filter(diagnosis => !!diagnosis),
      tap(diagnosis => this.upsertDiagnosisVoToEncounter(tenantId, patientId, diagnosis))
    );
  }

  getRxHistory(tenantId: string, patientId: string, rxType?: 'dispensed' | 'prescribed' | 'all' | null) {
    //return this.clinicalNewClient.getEncountersRxHistory(tenantId, patientId, rxType);
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider => this.merakiEncounterService.getEncountersRxHistory(provider.PracticeTenantId, patientId, rxType))
    );
  }

  getMedicineInforByATC5(tenantId: string, ingredient: string) {
    return this.clinicalNewClient.getMedicineInforByATC5(tenantId, ingredient);
  }

  getFormularyInformationForNappiAndSPO(tenantId: string, nappiCode: string, schemeCode: string, planCode: string, optionCode: string) {
    const formularyType = FormularyType._1; // chronic
    return this.clinicalNewClient.getFormularyInfo(tenantId, formularyType, nappiCode, schemeCode, planCode, optionCode);
  }

  priceConsumables(tenantId: string, patientId: string, consumables: ConsumableViewModel[]) {
    return this.providerService.specialityRule$.pipe(
      take(1),
      map(specialityRule => {
        let fakeEncounter = this.clinicalEncounterQuery.getEntity(patientId);

        return (fakeEncounter = {
          ...fakeEncounter,
          EncounterId: uuidV4(),
          EncounterLineItems: {
            ...fakeEncounter.EncounterLineItems,
            LineItems: consumables.map(
              (c, i) =>
                ({
                  LineType: 'Consumable',
                  LineNum: ++i,
                  ChargeCode: specialityRule.ConsumableProcedureCode,
                  ChargeDesc: c.ConsumableDescription,
                  NappiCode: c.NAPPIFullCode,
                  ChargeQuan: 1,
                  UnitPrice: null,
                  TotalExcVat: null,
                  TotalIncVat: null,
                  Amount: null,
                  PriceOverride: false,
                  CreatedFrom: c.NAPPIShortCode,
                  Parameters: {
                    CategoryCode: c.CategoryCode,
                    SubCategoryCode: c.SubCategoryCode,
                    Token: `C-${specialityRule.ConsumableProcedureCode}-${c.SubCategoryCode}`,
                  },
                }) as EncounterLineItemVo
            ),
          },
        });
      }),
      switchMap(encounter => this.priceEncounterForProvider(tenantId, encounter))
      //switchMap(encounter => this.clinicalClient.priceEncounter(tenantId, encounter.EncounterId, encounter))
    );
  }

  priceMedicines(tenantId: string, patientId: string, medicines: EncounterLineItemVo[]) {
    let fakeEncounter = this.clinicalEncounterQuery.getEntity(patientId);
    fakeEncounter = {
      ...fakeEncounter,
      EncounterId: uuidV4(),
      EncounterLineItems: {
        ...fakeEncounter.EncounterLineItems,
        LineItems: medicines.map(
          (c, i) =>
            ({
              LineNum: ++i,
              ...c,
              ChargeQuan: this.getEncounterLineQuantitySum(fakeEncounter, c),
              Diagnosis: fakeEncounter.EncounterLineItems.EncounterHeader.Diagnosis,
            }) as EncounterLineItemVo
        ),
      },
    };
    return this.priceEncounterForProvider(tenantId, fakeEncounter);
    //return this.clinicalClient.priceEncounter(tenantId, fakeEncounter.EncounterId, fakeEncounter);
  }

  priceMedicineLine(tenantId: string, patientId: string, medicines: EncounterLineItemVo[]) {
    let fakeEncounter = this.clinicalEncounterQuery.getEntity(patientId);
    fakeEncounter = {
      ...fakeEncounter,
      EncounterId: uuidV4(),
      EncounterLineItems: {
        ...fakeEncounter.EncounterLineItems,
        LineItems: medicines.map(
          (c, i) =>
            ({
              LineNum: ++i,
              ...c,
              Diagnosis: fakeEncounter.EncounterLineItems.EncounterHeader.Diagnosis,
            }) as EncounterLineItemVo
        ),
      },
    };

    const encounterUpdated = this.removeDosageInstructionFromEncounter(fakeEncounter);

    return this.priceEncounterForProvider(tenantId, encounterUpdated);
    //return this.clinicalClient.priceEncounter(tenantId, fakeEncounter.EncounterId, encounterUpdated);
  }

  getEnrichedMedicineInfoForPatient(tenantId: string, patientId: string, medicines: MedicineInfo[]) {
    return combineLatest([
      this.patientsService.patientAccountDetailsById$(tenantId, patientId),
      this.patientsService.patientConditionsById$(tenantId, patientId).pipe(
        map(conditions => (conditions && conditions.Conditions) || []),
        map(conditions => conditions.flatMap(condition => condition.Medications || [])),
        // only registered, return only NappiCode
        map(medications => medications.filter(medication => medication.IsRegistered).map(medication => medication.NappiCode))
      ),
    ]).pipe(
      map(([patientAccountInfo, registeredNappis]) => {
        const spoList = patientAccountInfo.IsCashAccount
          ? []
          : [patientAccountInfo.MedicalAidSchemeCode, patientAccountInfo.MedicalAidPlanCode, patientAccountInfo.MedicalAidOptionCode];

        const medicineViewModels = medicines.map(m => ({ MedicineInfo: m }) as MedicineViewModel);

        return enrichMedicineViewModelsInfoFlags(medicineViewModels, spoList, registeredNappis).map(m => m.MedicineInfo);
      })
    );
  }

  medicineInteractions$(patientId: string): Observable<MedicineInteractionVo[]> {
    return this.clinicalEncounterQuery.encounterUiState$(patientId).pipe(map(entity => entity?.medicineInteractions || []));
  }

  medicineInteractionStatus$(patientId: string): Observable<MedicineInteractionStatus> {
    return this.clinicalEncounterQuery
      .encounterUiState$(patientId)
      .pipe(map(entity => entity?.medicineInteractionStatus || MedicineInteractionStatus.None));
  }

  updateMedicineInteractions(patientId: string, tenantId: string, interactions: MedicineInteractionVo[]) {
    this.updateEncounterUiState(tenantId, patientId, { medicineInteractions: interactions });
  }

  loadSuggestiveMeds(tenantId: string, icd10codes: string[], patient: PatientVo) {
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(configs =>
        this.merakiLookupService.loadSuggestiveMeds(`${configs.PracticeNumber}-${configs.TreatingPracticeNumber}`, icd10codes, patient)
      ),
      map(meds => _.orderBy(Object.values(meds.suggested_meds), ['atc_combined_score'], ['desc']))
    );
  }

  renderKahunPatientSummary(practiceId: string, patientId: string) {
    return this.providerService.appointments$.pipe(
      map(app => app.find(f => f.PatientId === patientId)),
      switchMap(app =>
        this.merakiService.getKahunPatientSummary(practiceId, app?.CalendarEventXRef).pipe(
          map(m => ({
            ...m,
            appointmentId: app?.CalendarEventXRef,
          }))
        )
      ),
      tap(kAppointment => {
        if (!kAppointment) {
          return;
        }

        this.clinicalEncounterStore.ui.update(patientId, uiState => {
          const newState = {
            ...uiState,
            kahunAppointment: kAppointment,
          };
          return newState;
        });
      }),
      catchError(err => {
        console.error(err);
        return of(null);
      })
    );
  }

  addAdImpression(patientId: string, adImpression: AdImpression) {
    this.clinicalEncounterStore.ui.update(patientId, state => {
      const updState = {
        ...state,
        adImpressions: [...(state.adImpressions || []), adImpression],
      };
      return updState;
    });
  }

  private getAdImpressions(encounter: EncounterVo): AdImpression[] {
    const impressions = this.clinicalEncounterQuery.ui.getEntity(encounter.PatientId)?.adImpressions || [];
    const result: AdImpression[] = [];
    impressions.forEach(impression => {
      const existingImpression = result.find(a =>
        impression.IsDispensed ? a.NAPPICode10 === impression.NAPPICode10 : a.NAPPICode7 === impression.NAPPICode7
      );
      if (!existingImpression) {
        let hasMatchingLine = false;

        if (impression.IsDispensed) {
          hasMatchingLine = encounter.EncounterLineItems?.LineItems?.some(
            item => item.NappiCode && impression.NAPPICode10 && item.NappiCode.padStart(10, '0') === impression.NAPPICode10
          );
        } else {
          hasMatchingLine = encounter.MedicationsPrescriptions?.some(item =>
            item.MedicationPrescriptionLines?.some(line => line.NappiCode === impression.NAPPICode7)
          );
        }

        if (hasMatchingLine) {
          const encounterTimestamp = new Date();
          result.push({
            ...impression,
            EncounterTimestamp: encounterTimestamp,
            EncounterTimestampText: moment(encounterTimestamp).format('YYYY-MM-DD HH:mm'),
            Status: 'NEW',
          });
        }
      }
    });
    return result;
  }

  downloadQuestionnaire$(practiceId: string, encounterId: string, questionnaireTitle: string, patient: PatientVo) {
    return this.merakiEncounterService.getEncounter(practiceId, encounterId).pipe(
      map(encounter => ({
        questionnaire: encounter.Questionnaires.find(questionnaire => questionnaire.Title === questionnaireTitle),
        encounter,
      })),
      switchMap(({ questionnaire, encounter }) =>
        generateQuestionnairePdf$(questionnaire, patient, null).pipe(map(pdf => ({ pdf, encounter })))
      ),
      tap(({ pdf, encounter }) => {
        const filename = `${encounter.PatientEventDetails.Patient.PatientDetails.FirstName}_${encounter.PatientEventDetails.Patient.PatientDetails.Surname}_${questionnaireTitle}.pdf`;
        pdf.download(filename);
      })
    );
  }
}
